<script setup lang="ts">
import { ref, shallowRef, reactive, computed, watch, onMounted, onUnmounted, defineAsyncComponent } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table"
import { Pencil, Save, X, XCircle, Trash, Settings, Glasses, Download, File, Check, Loader2 } from "lucide-vue-next"
import axios from "axios"
import { useToast, Toaster } from "@/components/ui/toast"
import { useI18n } from 'vue-i18n';
import draggable from "vuedraggable"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import SingleSelect from './SingleSelect.vue'
import ManySelect from './ManySelect.vue'
const TiptapEditor = defineAsyncComponent(() => import('./TiptapEditor.vue'))
const EditorPanel = defineAsyncComponent(() => import('./EditorPanel.vue'))
import ColumnSelect from './ColumnSelect.vue'
import { storeToRefs } from "pinia"
import { useEntityStore } from "@/stores/useEntityStore"
import { useColumnStore } from "@/stores/columnStore"
import { useGefahrstoffePageCache } from "@/stores/gefahrstoffePageCache"
import { useViewStore } from "@/stores/viewStore"
import {
  ASSIGNMENT_SCOPE_DATASCOPE_SESSION_PREFIX,
  optionLabel,
  capitalize,
  get_data_attr,
  sortDropdownOptions,
  dropdownSortConfigForColumn,
} from '@/utils/globals'
import { columnsInOrder, restoreColumnOrderFromMove } from '@/utils/columnOrder'
import {
  columnCssVariableName,
  columnChangeRequiresDataReload,
  effectiveColumnWidth,
  MIN_COLUMN_WIDTH,
  mergeColumnsWithPresentation,
  setColumnWidth,
  stickyColumnCssVariables,
  supportsStickyColumns,
  type ColumnPresentationChange,
} from '@/utils/columnPresentation'
import { pairedMultiSelectPatch, pairedSelectPatch } from '@/utils/pairedSelect'
import { shouldForceOwnDatascope } from '@/utils/assignmentRoles'
import { hasEntityId } from '@/utils/entityIdentity'
import { useEventListener, useDebounceFn } from "@vueuse/core"

const { t, te, locale } = useI18n();
const userInfo = get_data_attr("user");
const customerInfo = get_data_attr("customer");

const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
if (token) axios.defaults.headers.common["X-CSRF-Token"] = token

// Set initial locale header
axios.defaults.headers.common['X-Locale'] = locale.value || 'de'

// Map i18n locale to date locale string
const dateLocale = computed(() => {
  const localeMap: Record<string, string> = {
    'de': 'de-DE',
    'en': 'en-US',
    'zh-TW': 'zh-TW'
  }
  return localeMap[locale.value] || 'de-DE'
})

interface ColumnDef {
  key: string
  label: string
  type?: "text" | "number" | "date" | "color" | "select" | "boolean" | "multi-select" | "multi" | "many"
  disabled?: boolean
  disabledReason?: string | null
  write?: boolean
  required?: boolean
  options?: any[]
  optionsEndpoint?: string
  paired_field?: string
  paired_multi?: boolean
  alwaysShowSearch?: boolean
  width?: number
  sticky?: number | string | boolean
  conditional_filter?: {
    controlling_key: string
    equals: string | number
    active_when_empty?: boolean
    combine_with_normal_when_empty?: boolean
    token_prefix?: string
    options: any[]
  }
}

const props = withDefaults(defineProps<{
  columns: any[]
  label: string
  labelSingular?: string
  logo?: string | null
  endpoint?: string
  saveendpoint?: string
  endpointpars?: Record<string, number>
  resetSessionFilterKey?: string | number | null
  enableSearch?: boolean
  enableAdd?: boolean
  enableEdit?: boolean
  enableDetail?: boolean
  enableDelete?: boolean
  enableFilter?: boolean
  enableColChange?: boolean
  enableColFilterSide?: boolean
  enableColFilterTop?: boolean
  enableAll?: boolean
  enableCopy?:boolean
  enableNotifications?:boolean
  removeDeletedRowLocally?: boolean
  alwaysEdit?: boolean
  withpaging?: boolean
  hideActionColumn?: boolean
  headerFontSize?: number
  singleSelectFontsize?: number
  data?: any[] 
  onDelete?: (id: number) => void
  /** When set, detail/editor panel uses these columns instead of the table column list (e.g. full spec, read-only). */
  editorPanelColumns?: any[]
  /** Optional list of column keys to hide in detail/editor panel while keeping table columns unchanged. */
  editorExcludeKeys?: string[]
  /** Detail modal: hide Save, read-only presentation (still require columns with write: false or use editorPanelColumns). */
  editorReadOnly?: boolean
  ownColumnKeys?: string[]
  /** Optional local persistence key for data-only tables that should remember column state. */
  columnStateKey?: string | null
  /** Optional local persistence key for column filter state. */
  filterStateKey?: string | null
  /** Assignment scope area whose profile mask should control the datascope filter. */
  assignmentScopeArea?: string | null
  /** When true, incoming column `show` values override saved visibility while preserving order/width. */
  forceSpecColumnVisibility?: boolean
  /** Width in px for the sticky row action column. */
  actionColumnWidth?: number
}>(), {
  editorPanelColumns: undefined,
  editorExcludeKeys: () => [],
  editorReadOnly: false,
  ownColumnKeys: () => [],
  columnStateKey: null,
  filterStateKey: null,
  assignmentScopeArea: null,
  forceSpecColumnVisibility: false,
  actionColumnWidth: 150,
  resetSessionFilterKey: null,
  saveendpoint: null,
  logo: null,
  enableSearch: false,
  enableAdd: true,
  enableEdit: true,
  enableDetail: true,
  enableDelete: true,
  enableFilter: true,
  enableColChange: true,
  enableColFilterSide: true,
  enableColFilterTop: false,
  enableAll: false,
  alwaysEdit: false,
  enableCopy: true,
  enableNotifications: false,
  removeDeletedRowLocally: false,
  withpaging: true,
  hideActionColumn: false,
})

props.columns.forEach(col => {
  col.width = col.width ? parseInt(col.width) : 150
})

const columnStore = useColumnStore()
const gefahrstoffePageCache = useGefahrstoffePageCache()
const viewStore = useViewStore()
const dataVersion = ref(0)
const rowVersions = reactive<Record<string | number, number>>({})
const columnToggleSeq = ref(0)
const ACTION_COLUMN_WIDTH = 150
const STICKY_COLUMN_OVERLAP_PX = 1
const actionColumnWidthPx = computed(() => Number(props.actionColumnWidth || ACTION_COLUMN_WIDTH))

function translateLabel(label: any) {
  const key = String(label ?? "")
  return key && te(key) ? t(key) : key
}

/** Build the current spec column list (actions + props.columns + buffer). */
function buildSpecColumns() {
  const spec = [
    ...(props.hideActionColumn
      ? []
      : [{ key: "actions", label: t("view.actions"), fixed: true }]),
    ...props.columns.map(c => ({ ...c })),
    // { key: "buffer", label: "Puffer" }
  ]

  if (props.endpoint && String(props.endpoint).includes('pflichten-sub-assignment-')) {
    console.log('[DataTable buildSpecColumns SNAPSHOT]', JSON.parse(JSON.stringify(spec)))
  }

  return spec
}

const specColumnsComputed = computed(() => buildSpecColumns())
const columnPersistenceKey = computed(() => {
  if (props.columnStateKey) return props.columnStateKey
  if (!props.endpoint || String(props.endpoint).includes('-assignment-')) return null
  return props.endpoint
})
const usesServerColumnWidths = computed(() => Boolean(props.endpoint) && !props.columnStateKey)
const optimisticColumnOrder = shallowRef<string[] | null>(null)
const stickyColumnOverrides = reactive<Record<string, number>>({})

const columns = computed({
  get() {
    // Data-only assignment tables can opt into local persistence via columnStateKey.
    const persistenceKey = columnPersistenceKey.value
    const saved = persistenceKey ? columnStore.getColumns(persistenceKey) : null
    const specColumns = specColumnsComputed.value
    const withStickyOverrides = (orderedColumns: any[]) => orderedColumns.map((column: any) => {
      const stickyOverride = stickyColumnOverrides[String(column.key)]
      return stickyOverride === undefined ? column : { ...column, sticky: stickyOverride }
    })

    if (!saved) {
      // The specification is already the source of truth for defaults. Avoid a
      // synchronous Pinia/localStorage write while the table is first rendering.
      return withStickyOverrides(columnsInOrder(specColumns, optimisticColumnOrder.value))
    }

    const merged = mergeColumnsWithPresentation(specColumns, saved, {
      preferSpecWidth: usesServerColumnWidths.value,
      forceSpecVisibility: props.forceSpecColumnVisibility,
    })
    // Do not write merged back here: order and show are updated by setter (reorder) and onToggleColumn (toggle).
    // Writing here would overwrite those updates with a new array reference and could revert to stale state.
    return withStickyOverrides(columnsInOrder(merged, optimisticColumnOrder.value))
  },
  set(val) {
    // vuedraggable writes the optimistic order here. Persist it only after the
    // browser has painted the aligned table so localStorage cannot delay the drop.
    optimisticColumnOrder.value = val.map((column: any) => String(column.key))
  }
})

const stableKey = computed(() => props.endpoint ?? 'static')
const columnOrderKey = computed(() => columns.value.map(c => `${c.key}:${c.show}`).join(','))

/** Columns passed to EditorPanel (glasses): optional full spec for task-linked RG, else current table columns. */
const editorColumnsForPanel = computed(() => {
  const extra = props.editorPanelColumns
  const base = (extra && extra.length > 0) ? extra : columns.value
  const hidden = new Set((props.editorExcludeKeys || []).map((k) => String(k)))
  if (hidden.size === 0) return base
  return base.filter((c: any) => !hidden.has(String(c?.key ?? c?.field ?? "")))
})

const editorPanelTitle = computed(() => {
  const lbl = props.label
  if (props.editorReadOnly) {
    return translateLabel(lbl)
  }
  return translateLabel(lbl) + " bearbeiten"
})

const visibleColumns = computed(() => {
  void columnToggleSeq.value
  return columns.value.filter(c => c.show || c.key === 'actions')
})

const isGefahrstoffeTable = computed(() => props.endpoint === "gefahrstoffe")
const isStickyColumnTable = computed(() => supportsStickyColumns(props.endpoint))
const columnWidthOverrides = reactive<Record<string, number>>({})
const displayColumnWidth = (column: any) => effectiveColumnWidth(column, columnWidthOverrides)
const visibleDataColumns = computed(() => visibleColumns.value.filter((column: any) => (
  column.key !== "actions" && column.key !== "buffer"
)))
const enforcesConfiguredWidths = computed(() => props.enableColChange && usesServerColumnWidths.value)
const configuredTableWidth = computed(() => {
  if (!enforcesConfiguredWidths.value && !isStickyColumnTable.value) return null
  const actionWidth = props.hideActionColumn ? 0 : actionColumnWidthPx.value
  return actionWidth + visibleDataColumns.value.reduce(
    (total: number, column: any) => total + displayColumnWidth(column),
    0
  )
})
const dataTableStyle = computed(() => {
  if (!configuredTableWidth.value) return undefined
  const width = `${configuredTableWidth.value}px`
  return { width, minWidth: width }
})

function isStickyDataColumn(col: any) {
  return isStickyColumnTable.value && col.key !== "actions" && col.show !== false && Number(col.sticky || 0) > 0
}

const stickyColumnLeftByKey = computed(() => {
  const leftByKey: Record<string, number> = {}
  if (!isStickyColumnTable.value) return leftByKey

  let left = props.hideActionColumn ? 0 : actionColumnWidthPx.value - (STICKY_COLUMN_OVERLAP_PX * 2)
  for (const col of visibleColumns.value) {
    if (col.key === "actions") continue
    if (!isStickyDataColumn(col)) continue
    leftByKey[col.key] = left
    left += displayColumnWidth(col) - STICKY_COLUMN_OVERLAP_PX
  }
  return leftByKey
})

function stickyDataCellStyle(col: any, section: "header" | "filter" | "body" = "body", rowIndex = 0, highlighted = false) {
  if (!isStickyDataColumn(col)) return {}

  const zIndexBySection = {
    header: 1001,
    filter: 1000,
    body: 998
  }
  const bodyBackground = highlighted ? "#fefce8" : (rowIndex % 2 === 0 ? "#ffffff" : "#f9fafb")
  const backgroundBySection = {
    header: "#f3f4f6",
    filter: "#f9fafb",
    body: bodyBackground
  }

  return {
    position: "sticky",
    left: `${stickyColumnLeftByKey.value[col.key] || 0}px`,
    zIndex: zIndexBySection[section],
    backgroundColor: backgroundBySection[section]
  }
}

function bodyDataCellStyle(col: any, rowIndex: number, highlighted = false) {
  const key = String(col.key)
  const width = `var(${columnCssVariableName(key, "width")})`
  const style: Record<string, string> = {
    width,
    minWidth: width,
    maxWidth: width,
  }

  if (!isStickyColumnTable.value) return style

  style.position = `var(${columnCssVariableName(key, "sticky-position")}, static)`
  style.left = `var(${columnCssVariableName(key, "sticky-left")}, auto)`
  style.zIndex = `var(${columnCssVariableName(key, "sticky-z-index")}, auto)`
  const rowBackground = rowIndex % 2 === 0 ? "255, 255, 255" : "249, 250, 251"
  style.backgroundColor = highlighted
    ? "#fefce8"
    : `rgba(${rowBackground}, var(${columnCssVariableName(key, "sticky-background-opacity")}, 0))`
  return style
}

function actionColumnStyle(section: "header" | "filter" | "body" = "body") {
  const width = actionColumnWidthPx.value
  const zIndexBySection = {
    header: 1002,
    filter: 1001,
    body: 999
  }
  const backgroundBySection = {
    header: "#f3f4f6",
    filter: "#f9fafb",
    body: "#ffffff"
  }

  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    left: `-${STICKY_COLUMN_OVERLAP_PX}px`,
    zIndex: zIndexBySection[section],
    backgroundColor: backgroundBySection[section]
  }
}

watch(() => props.columns, (newCols) => {
  newCols.forEach((col) => {
    const localCol = columns.value.find(c => c.key === col.key)
    if (localCol) {
      localCol.label = col.label
      localCol.type = col.type
      localCol.write = col.write
      localCol.disabled = col.disabled
      localCol.disabledReason = col.disabledReason
      localCol.options = col.options
      localCol.width = col.width ?? localCol.width ?? 150
      localCol.custom = col.custom
    }
  })
}, { deep: true })

const rows = shallowRef<any[]>([])
const isLoading = ref(false)
const isRefreshing = ref(false)
const isSortLoading = ref(false)
const loadErrorMessage = ref("")
const refreshWarningMessage = ref("")
const editingRowId = ref<string | number | null>(null)
const editingRow = ref<Object>(null)
const autoOpenSelectCell = ref<{ rowId: string | number; colKey: string } | null>(null)
const isAddingRow = ref(false)
const deletingRowIds = ref<Set<string | number>>(new Set())
const validationErrors = ref<Record<string, string>>({})
const { toast } = useToast()

// Server-side state
const serverSearch = ref("")
const serverColumnFilters = ref<Record<string, any>>({})
const serverSortKey = ref<string | null>(null)
const serverSortAsc = ref(true)

// Look for a stored page or default to 1
const storageKey = `table-page-${props.endpoint}`;
const serverPage = ref(parseInt(sessionStorage.getItem(storageKey) || "1"));

// Watch for changes and save to storage
watch(serverPage, (newPage) => {
  sessionStorage.setItem(storageKey, newPage.toString());
});

const serverPageSize = ref(props.endpoint === "gefahrstoffe" ? 20 : 50)
const totalRows = ref(0)
const totalRowsKnown = ref(true)
const hasMoreRows = ref(false)
const countUnavailable = ref(false)
const firstTime = ref(true);
const loadRequestSeq = ref(0)
const countRequestSeq = ref(0)
const consumedResetSessionFilterKey = ref<string | number | null>(null)
let activeLoadAbortController: AbortController | null = null
let activeCountAbortController: AbortController | null = null
// Dynamic select options
const dynamicOptions = ref<Record<string, any[]>>({})
const dynamicOptionRequests = new Map<string, Promise<any>>()

const emit = defineEmits(['swapcol', 'page-loaded', 'cell-changed', 'cell-notified', 'data-mutated', 'request-add', 'request-edit', 'row-deleted']);



function getNested(obj: any, path: string) {
	// console.log("assignment=" + JSON.stringify(obj["assignment"]))
  
	// console.log("getNested path=" + path)
  let psplit = path.split(".");

  let val = psplit.reduce((o, k) => (o ? o[k] : undefined), obj)

//   console.log("val=" + val)
  return psplit.reduce((o, k) => (o ? o[k] : undefined), obj)
}

function isBooleanValue(value: any): boolean {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "Ja" || value === "Yes") {
    return true
  }
  return false
}

function formatCellValue(value: any): any {
  if (value == null) return ""
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return value
}

function isEmptyFilterValue(value: any) {
  if (Array.isArray(value)) return value.length === 0
  return value === null || value === undefined || value === ""
}

function normalizedFilterState() {
  return Object.entries(serverColumnFilters.value).reduce((acc, [key, value]) => {
    if (managesAssignmentDatascope() && key === "datascope") return acc
    if (!isEmptyFilterValue(value)) acc[key] = value
    return acc
  }, {} as Record<string, any>)
}

const restoredPersistedFilterState = ref(false)

function selectedOptionsFromFilters(filters: Record<string, any>) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) acc[key] = [...value]
    return acc
  }, {} as Record<string, any[]>)
}

const profileAssignmentScopeMode = computed(() => {
  if (!props.assignmentScopeArea) return null
  return viewStore.assignmentScopeMode(props.assignmentScopeArea)
})

function managesAssignmentDatascope() {
  return Boolean(props.assignmentScopeArea)
}

function assignmentDatascopeSessionKey() {
  if (!managesAssignmentDatascope()) return null

  const userId = userInfo?.id ?? userInfo?.user_id ?? "anonymous"
  const tableKey = props.filterStateKey || props.endpoint || props.label || "table"
  return `${ASSIGNMENT_SCOPE_DATASCOPE_SESSION_PREFIX}:${userId}:${props.assignmentScopeArea}:${tableKey}`
}

function normalizeDatascopeFilterValue(value: any) {
  if (isEmptyFilterValue(value)) return []

  const values = Array.isArray(value) ? value : [value]
  return values.map((item) => {
    const numeric = Number(item)
    return Number.isNaN(numeric) ? item : numeric
  })
}

function readSessionDatascopeOverride(): { present: boolean; value: any[] } {
  const key = assignmentDatascopeSessionKey()
  if (!key) return { present: false, value: [] }

  const raw = sessionStorage.getItem(key)
  if (raw === null) return { present: false, value: [] }

  try {
    return { present: true, value: normalizeDatascopeFilterValue(JSON.parse(raw)) }
  } catch {
    sessionStorage.removeItem(key)
    return { present: false, value: [] }
  }
}

function hasSessionDatascopeOverride() {
  return readSessionDatascopeOverride().present
}

function persistSessionDatascopeOverride(value: any) {
  const key = assignmentDatascopeSessionKey()
  if (!key) return

  sessionStorage.setItem(key, JSON.stringify(normalizeDatascopeFilterValue(value)))
}

function applyDatascopeFilterToState(value: any) {
  const datascope = normalizeDatascopeFilterValue(value)
  if (datascope.length === 0) {
    delete selectedOptions.value["datascope"]
    delete serverColumnFilters.value["datascope"]
    return true
  }

  selectedOptions.value["datascope"] = [...datascope]
  serverColumnFilters.value["datascope"] = [...datascope]
  return true
}

function applySessionDatascopeFilterToVisibleState() {
  const override = readSessionDatascopeOverride()
  if (!override.present) return false

  applyDatascopeFilterToState(override.value)
  return true
}

function profileDatascopeFilterValue() {
  if (profileAssignmentScopeMode.value === "own") return [1]
  if (profileAssignmentScopeMode.value === "all") return [0]
  return null
}

function applyProfileDatascopeFilter(filters: Record<string, any>) {
  const value = profileDatascopeFilterValue()
  if (!value) return false
  filters["datascope"] = [...value]
  return true
}

function applyProfileDatascopeFilterToVisibleState() {
  const value = profileDatascopeFilterValue()
  if (!value) return false
  return applyDatascopeFilterToState(value)
}

function applyPreferredDatascopeFilter(filters: Record<string, any>) {
  const override = readSessionDatascopeOverride()
  if (override.present) {
    if (override.value.length === 0) {
      delete filters["datascope"]
    } else {
      filters["datascope"] = [...override.value]
    }
    return true
  }

  return applyProfileDatascopeFilter(filters)
}

function applyPreferredDatascopeFilterToVisibleState() {
  if (applySessionDatascopeFilterToVisibleState()) return true
  return applyProfileDatascopeFilterToVisibleState()
}

function persistFilterState() {
  if (!props.filterStateKey) return
  const state = normalizedFilterState()
  if (Object.keys(state).length === 0) {
    localStorage.removeItem(props.filterStateKey)
    restoredPersistedFilterState.value = false
  } else {
    localStorage.setItem(props.filterStateKey, JSON.stringify(state))
    restoredPersistedFilterState.value = true
  }
}

function loadPersistedFilterState() {
  if (!props.filterStateKey) return false

  try {
    const raw = localStorage.getItem(props.filterStateKey)
    if (!raw) {
      restoredPersistedFilterState.value = false
      return false
    }

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return false

    const restored = Object.entries(parsed).reduce((acc, [key, value]) => {
      if (managesAssignmentDatascope() && key === "datascope") return acc
      if (!isEmptyFilterValue(value)) acc[key] = value
      return acc
    }, {} as Record<string, any>)
    applyPreferredDatascopeFilter(restored)

    if (Object.keys(restored).length === 0) {
      restoredPersistedFilterState.value = false
      return false
    }

    serverColumnFilters.value = restored
    selectedOptions.value = selectedOptionsFromFilters(restored)
    if (reconcileConditionalFilterState()) persistFilterState()
    restoredPersistedFilterState.value = true
    return true
  } catch (error) {
    console.warn("Could not load persisted table filters", error)
    localStorage.removeItem(props.filterStateKey)
    restoredPersistedFilterState.value = false
    return false
  }
}

function hasActivePersistedFilterState() {
  return Boolean(props.filterStateKey && restoredPersistedFilterState.value && Object.keys(normalizedFilterState()).length > 0)
}

function setNested(obj: any, path: string, value: any, event?: Event) {
  const previousValue = getNested(obj, path);
  const keys = path.split(".");

  const applyValue = (nextValue: any) => {
    let o = obj;

    keys.slice(0, -1).forEach((k) => {
      if (!o[k]) o[k] = {};
      o = o[k];
    });

    o[keys[keys.length - 1]] = nextValue;
    // Keep v-memo rows reactive: bump per-row version after every field edit.
    if (obj && obj.id != null) {
      rowVersions[obj.id] = (rowVersions[obj.id] || 0) + 1
    }
  }

  applyValue(value);

  emit("cell-changed", {
    id: obj.id,
    path: path,  // send as array
    value: value,
    previousValue,
    rollback: () => applyValue(previousValue),
	  event: event,
  });
}

async function uploadFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/kunde/', {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data.url; // e.g., the uploaded file URL returned by your backend
}

function onFileChange(event: Event, key: string, currentItem) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] || null;

  if (file) {
    // Store the file temporarily in your currentItem
    setNested(currentItem, key, file);
  } else {
    // Clear if no file selected
    setNested(currentItem, key, null);
  }
}


function onCheckboxChange(event: Event, row: any, key: string) {
  const input = event.target as HTMLInputElement;
  const newValue = input.checked;

  try {
    setNested(row, key, newValue, event);
  } catch (err) {
    console.error(err);
    input.checked = !newValue;
  }	
}


async function loadDynamicOptions() {
  const visibleKeys = new Set(visibleColumns.value.map((col: any) => col.key))
  const loads = props.columns
    .filter((col) => visibleKeys.has(col.key))
    .filter((col) => col.type === "select" && col.optionsEndpoint)
    .map(async (col) => {
      const endpoint = String(col.optionsEndpoint)
      let request = dynamicOptionRequests.get(endpoint)
      if (!request) {
        request = axios.get(endpoint)
        dynamicOptionRequests.set(endpoint, request)
      }

      try {
        const { data } = await request
        dynamicOptions.value[col.key] = sortedOptionsForColumn(col, data)
      } catch (error) {
        dynamicOptionRequests.delete(endpoint)
        console.warn(`Failed to load options for ${col.key}`, error)
      }
    })

  await Promise.allSettled(loads)
  if (props.endpoint === "gefahrstoffe") performance.mark("gefahrstoffe-options-ready")
}

function pendingResetSessionFilterKey() {
  const key = props.resetSessionFilterKey
  if (key === null || key === undefined || key === "" || key === 0) return null
  return key === consumedResetSessionFilterKey.value ? null : key
}

function clearServerFilterStateForSessionReset() {
  serverSearch.value = ""
  serverColumnFilters.value = {}
  serverPage.value = 1
}

function restoreDatascopeFilterForSessionReset(value: any) {
  if (isEmptyFilterValue(value)) return
  const datascope = Array.isArray(value) ? [...value] : [value]
  serverColumnFilters.value["datascope"] = datascope
  selectedOptions.value["datascope"] = datascope
}

function buildQueryParams() {
  const resetSessionFilterKey = pendingResetSessionFilterKey()
  const shouldResetSessionFilter = resetSessionFilterKey !== null && !hasActivePersistedFilterState()
  const datascopeBeforeReset = serverColumnFilters.value["datascope"]
  if (shouldResetSessionFilter) {
    clearServerFilterStateForSessionReset()
    restoreDatascopeFilterForSessionReset(datascopeBeforeReset)
  }

  const params: Record<string, any> = {
    page: serverPage.value,
    per_page: serverPageSize.value,
  }
  if (props.endpoint === "gefahrstoffe") {
    params.columns = columns.value
      .filter((column) => column.key !== "actions" && column.show !== false)
      .map((column) => column.key)
  }
  if (shouldResetSessionFilter) {
    params["reset_filter_button"] = 1
  }
  if (props.endpointpars) {
	for (const [key, value] of Object.entries(props.endpointpars)) {
	  if (value !== null && value !== "") params[key] = value
	}	
  }
  if (serverSearch.value) {
	if (serverSearch.value === "reset_filter") {
		params["reset_filter"] = 1
	} else if (serverSearch.value === "reset_filter_button") {
		params["reset_filter_button"] = 1
	} else {
		params.q = serverSearch.value		
	}
  } 
  if (serverSortKey.value) {
    params.sort_by = serverSortKey.value
    params.sort_order = serverSortAsc.value ? "0" : "1"
  }
  for (const [key, value] of Object.entries(serverColumnFilters.value)) {
    if (Array.isArray(value) && value.length === 0) {
      // Keep the key in query params so the backend can clear persisted session filters.
      params[key] = [""]
    } else if (value !== null && value !== "") {
      params[key] = value
    }
  }
  // Only apply backend "reset_filter" on the initial page load without any active search/sort/filter.
  // When the user starts typing fast, in-flight requests can race; this guard prevents accidentally
  // including reset_filter on subsequent (user-driven) requests while the initial load is unresolved.
  const hasAnyUserQuery =
    Boolean(serverSearch.value) ||
    Boolean(serverSortKey.value) ||
    Object.keys(serverColumnFilters.value).length > 0

  if (firstTime.value && !hasAnyUserQuery && !shouldResetSessionFilter) {
    params["reset_filter"] = 1
  }
  return params
}

function stableCacheValue(value: any): any {
  if (Array.isArray(value)) return value.map(stableCacheValue)
  if (value && typeof value === "object") {
    return Object.keys(value).sort().reduce((result: Record<string, any>, key) => {
      result[key] = stableCacheValue(value[key])
      return result
    }, {})
  }
  return value
}

function gefahrstoffeCacheKey(queryParams: Record<string, any>) {
  const cacheQuery = { ...queryParams }
  // This parameter is an instruction to clear server-side filter state, not a
  // property of the resulting page. Excluding it lets the equivalent initial
  // and revisit requests share one cache entry.
  delete cacheQuery.reset_filter
  return JSON.stringify(stableCacheValue({
    version: 1,
    userId: userInfo?.id,
    customerId: customerInfo?.id ?? userInfo?.customer_id,
    standortId: viewStore.currentStandort,
    locale: locale.value,
    query: cacheQuery,
  }))
}

async function loadGefahrstoffeCount(queryParams: Record<string, any>, listRequestId: number) {
  const requestId = ++countRequestSeq.value
  activeCountAbortController?.abort()
  activeCountAbortController = new AbortController()
  totalRowsKnown.value = false
  countUnavailable.value = false

  try {
    const countParams = { ...queryParams }
    delete countParams.page
    delete countParams.per_page
    delete countParams.sort_by
    delete countParams.sort_order
    const response = await axios.get("/gefahrstoffe/count.json", {
      params: countParams,
      signal: activeCountAbortController.signal,
    })
    if (requestId !== countRequestSeq.value || listRequestId !== loadRequestSeq.value) return

    totalRows.value = Number(response.data?.count || 0)
    totalRowsKnown.value = true
    performance.mark("gefahrstoffe-count-ready")
  } catch (error: any) {
    const canceled = error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || axios.isCancel?.(error)
    if (canceled || requestId !== countRequestSeq.value) return
    countUnavailable.value = true
  }
}


async function loadServerData() {
  loadErrorMessage.value = ""
  refreshWarningMessage.value = ""
  if (props.data) {
    console.log("just with dataprop=" + JSON.stringify(props.data.map(d => d.assignment)));
    rows.value = props.data.map(r => ({ ...r }))
    dataVersion.value++
    refreshDatascopeFilterOptions()
    totalRows.value = props.data.length;
    isLoading.value = false
    return
  }
  console.log("loadServerData()=" + props.endpoint)
  clearDisabledColumnFilters()
  const queryParams = buildQueryParams()
  const isGefahrstoffe = props.endpoint === "gefahrstoffe"
  const cacheKey = isGefahrstoffe ? gefahrstoffeCacheKey(queryParams) : ""
  const cachedPage = isGefahrstoffe ? gefahrstoffePageCache.getFresh(cacheKey) : null

  if (cachedPage) {
    rows.value = cachedPage.rows.map((row: any) => ({ ...row }))
    hasMoreRows.value = cachedPage.hasMore
    totalRowsKnown.value = false
    isLoading.value = false
    isRefreshing.value = true
    dataVersion.value++
    performance.mark("gefahrstoffe-cached-rows-shown")
  } else {
    isLoading.value = true
    isRefreshing.value = false
  }
  if (!cachedPage && !isSortLoading.value) {
    rows.value = []
  }
  const requestId = ++loadRequestSeq.value

  // Abort the previous request to avoid "last response wins" racing when typing quickly.
  activeLoadAbortController?.abort()
  activeLoadAbortController = new AbortController()
  const signal = activeLoadAbortController.signal

  let data: any[] = []
  let headers: Record<string, any> = {}
  const resetSessionFilterKeyForRequest = pendingResetSessionFilterKey()
  try {
    const res = await axios.get(props.endpoint + ".json", {
      params: queryParams,
      signal,
    })
    data = res.data
    headers = res.headers || {}
  } catch (err: any) {
    const isCanceled =
      Boolean(err?.name === "CanceledError") ||
      Boolean(err?.code === "ERR_CANCELED") ||
      Boolean(axios.isCancel?.(err))
    if (isCanceled || requestId !== loadRequestSeq.value) return
    console.error("loadServerData() failed", err)
    if (!cachedPage) {
      rows.value = []
      totalRows.value = 0
    }
    const status = err?.response?.status
    if (cachedPage) {
      refreshWarningMessage.value = "Gespeicherte Daten werden angezeigt; Aktualisierung fehlgeschlagen."
      countUnavailable.value = true
    } else if (status === 403) {
      loadErrorMessage.value = "403 Keine Berechtigung"
    } else {
      const rawMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.statusText ||
        err?.message ||
        "Fehler beim Laden"
      const message = typeof rawMessage === "string" ? rawMessage : "Fehler beim Laden"
      loadErrorMessage.value = status ? `${status} ${message}` : message
    }
    return
  } finally {
    if (requestId === loadRequestSeq.value) {
      isLoading.value = false
      isRefreshing.value = false
    }
  }

  // If a newer request was triggered, ignore this response.
  if (requestId !== loadRequestSeq.value) return

  if (resetSessionFilterKeyForRequest !== null) {
    consumedResetSessionFilterKey.value = resetSessionFilterKeyForRequest
  }

  // Attach hitMap to each row
  rows.value = data.map((r: any) => ({ ...r }))
  isSortLoading.value = false
  refreshDatascopeFilterOptions()

  if (isGefahrstoffe) {
    hasMoreRows.value = String(headers["x-has-more"] || "false") === "true"
    totalRows.value = data.length
    totalRowsKnown.value = false
    gefahrstoffePageCache.put(cacheKey, data, hasMoreRows.value)
    performance.mark("gefahrstoffe-fresh-rows-shown")
    void loadGefahrstoffeCount(queryParams, requestId)
  } else {
    totalRows.value = parseInt(headers["x-total-count"] || String(data.length))
    totalRowsKnown.value = true
  }
  firstTime.value = false
  console.log("emit page-loaded=" + data.length)
  emit('page-loaded', data);
}

watch(
  () => props.data,
  (newData) => {
    // Static data tables should refresh when their data prop changes.
    // Endpoint-backed tables do their initial load in onMounted after filters are initialized.
    if (newData !== undefined) {
      loadServerData();
    }
  },
  { immediate: true }
);

// Watch locale changes to refetch data
watch(
  () => locale.value,
  (newLocale) => {
    axios.defaults.headers.common['X-Locale'] = newLocale
    // Refetch data when locale changes (only if using endpoint, not static data)
    if (props.endpoint && !props.data) {
      loadServerData()
    }
  }
)

// Refetch data when query parameters (e.g. id_rechtsgrundlagen) change.
// This is required for tabbed views: without it, the table would keep stale rows.
watch(
  () => (props.endpointpars ? JSON.stringify(props.endpointpars) : ''),
  () => {
    if (props.endpoint && !props.data) {
      if (consumeGlobalSearchNavigation()) {
        clearDatascopeFilter()
      } else {
        forceEigeneDatascopeFilter()
      }
      serverPage.value = 1
      loadServerData()
    }
  },
)



function sortBy(key: string) {
  if (columns.value.find(col => col.key === key)?.disabled) return
  if (isSortLoading.value) return

  if (serverSortKey.value === key) {
    serverSortAsc.value = !serverSortAsc.value
  } else {
    serverSortKey.value = key
    serverSortAsc.value = true
  }
  isSortLoading.value = true
  serverPage.value = 1
  loadServerData() // only reload rows, not columns
}

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  serverPage.value = page
  loadServerData()
}

function onPageSizeChange() {
  serverPage.value = 1
  loadServerData()
}

const debouncedLoadAfterFilter = useDebounceFn(() => {
  loadServerData()
}, 250, { maxWait: 1000 })

function clearDisabledColumnFilters() {
  let changed = false
  for (const col of columns.value) {
    if (!col.disabled) continue
    if (col.key in serverColumnFilters.value || col.key in selectedOptions.value) changed = true
    delete serverColumnFilters.value[col.key]
    delete selectedOptions.value[col.key]
    if (serverSortKey.value === col.key) serverSortKey.value = ""
    if (autoOpenSelectCell.value?.colKey === col.key) autoOpenSelectCell.value = null
  }
  if (changed) persistFilterState()
}

function onColumnFilterChange(key: string, value: any) {
  if (columns.value.find(col => col.key === key)?.disabled) return
  if (key === "datascope") {
    persistSessionDatascopeOverride(value)
  }
  serverColumnFilters.value[key] = value
  clearIncompatibleConditionalDependentFilters(key, value)
  persistFilterState()
  serverPage.value = 1
  debouncedLoadAfterFilter()
}

function isFilterSet() {
  return Object.keys(serverColumnFilters.value).length > 0
}

function getFilterButtonColor() {
  let color = isFilterSet() ? 'red' : 'green'
  return `text-${color}-600 hover:bg-${color}-50`
}

function getFilterButtonText() {
  return isFilterSet() ? t('view.reset_filter')  : t('view.cleared_filter')
}

const onSearchChange = useDebounceFn((value: string) => {
  serverSearch.value = value
  serverPage.value = 1
  loadServerData()
}, 300, { maxWait: 1000 })

function resetFilters() {
  if (props.endpoint === "gefahrstoffe") gefahrstoffePageCache.clear()
  selectedOptions.value = {}
  serverSearch.value = "reset_filter_button"
  serverColumnFilters.value = {}
  if (props.filterStateKey) {
    localStorage.removeItem(props.filterStateKey)
    restoredPersistedFilterState.value = false
  }
  if (managesAssignmentDatascope()) {
    forceEigeneDatascopeFilter()
  }
  serverPage.value = 1
  loadServerData()
}

function filterWriteable(k, v) {
   if (String(props.endpoint) === "kunde" && k === "import_standort_id") return true
   return props.columns.find(c => c.key === k && c.write && !c.disabled)
}

async function saveRow(row: any) {
  console.log("saveRow(row: any)=" + JSON.stringify(row))
  const rowid = row.id
  const previousRow = hasEntityId(row) && !row.isNew ? cloneRow(editingRow.value || row) : null
  let responseData: any = null
  try {
    if (!hasEntityId(row) || row.isNew) {
      const res = await axios.post(props.endpoint + ".json", row)
      responseData = res?.data
      const msg = res?.data?.message
      toast({ title: "Saved", description: msg || "Row saved successfully." })
      loadServerData()
    } else {
	  let filteredEntries = Object.entries(row).filter(([k, v]) => filterWriteable(k, v)); 
      const saveEntity = props.saveendpoint || props.endpoint
      const res = await axios.put(`${saveEntity}/${rowid}.json`, savePayload(saveEntity, filteredEntries as [string, any][]))
      responseData = res?.data
      const msg = res?.data?.message
      mergeLocalRow(rowid, row, res?.data, previousRow)
      toast({ title: "Saved", description: msg || "Row saved successfully." })
    }
    emit('data-mutated', props.endpoint, responseData)
    autoOpenSelectCell.value = null
    editingRowId.value = null
    editingRow.value = null
    isAddingRow.value = false
  } catch (error: any) {
	if (previousRow && rowid !== null && rowid !== undefined) {
	  restoreLocalRow(rowid, previousRow)
	  editingRowId.value = rowid
	  editingRow.value = cloneRow(previousRow)
	}
	autoOpenSelectCell.value = null
	console.log("error=" + JSON.stringify(error.response))
	const message =
	  error.response?.data?.message ||
	  error.response?.data?.error ||
	  error.response?.data ||
	  error.response?.statusText ||
	  "Failed to save row."
    toast({ title: "Error", description: message, variant: "destructive" })
  }
}

function defaultVal4Col(col) {
	if (col.default) {
		return col.default
	}
    if (col.type === "number") {
	    return 0;
	}
    if (col.type === "many") {
	    return [];
	}
	return "";
}

function addRow() {
  if (props.endpoint === 'gefahrstoffe') {
    emit('request-add')
    return
  }
  
  const tempRow: any = { id: `new-${Date.now()}`, isNew: true }
  props.columns.forEach(col => tempRow[col.key] = defaultVal4Col(col))
  rows.value = [tempRow, ...rows.value]
  autoOpenSelectCell.value = null
  editingRowId.value = tempRow.id
  isAddingRow.value = true
}

async function editRow(row: any) {
  if (props.endpoint === 'gefahrstoffe') {
    emit('request-edit', row)
    return
  }
  
  autoOpenSelectCell.value = null
  editingRowId.value = row.id
  editingRow.value = JSON.parse(JSON.stringify(row))
}

function isRowDeleting(id: number | string) {
  return deletingRowIds.value.has(id)
}

function setRowDeleting(id: number | string, deleting: boolean) {
  const next = new Set(deletingRowIds.value)
  if (deleting) {
    next.add(id)
  } else {
    next.delete(id)
  }
  deletingRowIds.value = next
}

async function deleteRow(id: number | string) {
  if (props.removeDeletedRowLocally && isRowDeleting(id)) return

  if (props.onDelete) {
	console.log("props.onDelete=" + id)
	return props.onDelete(id)
  }
  try {
	const confirmMsg =
	  props.endpoint === 'users'
	    ? t('view.user_set_inactive_confirm')
	    : translateLabel(props.label) + " Löschen? Kann nicht wiederrufen werden."
	const ok = await confirm(confirmMsg)
	if (ok) {
		if (props.removeDeletedRowLocally) setRowDeleting(id, true)
		await axios.delete(`${props.endpoint}/${id}.json`)
		if (props.removeDeletedRowLocally) {
			setRowDeleting(id, false)
			removeLocalRow(id)
			emit('row-deleted', id)
		} else {
			loadServerData()
		}
		emit('data-mutated', props.endpoint)
		if (props.endpoint === 'users') {
		  toast({ title: t('view.user_set_inactive_title'), description: t('view.user_set_inactive_desc') })
		} else {
		  toast({ title: "Deleted", description: `Row ${id} removed.` })
		}
	}	
	  isAddingRow.value = false
  } catch (error: any) {
	const message =
	error.response?.data?.error ||
	error.response?.data?.message || // if your API returns JSON with "message"
	error.response?.statusText ||   // fallback to status text
	"Failed to delete row."
    toast({ title: "Error", description: message, variant: "destructive" })
  } finally {
    if (props.removeDeletedRowLocally) setRowDeleting(id, false)
  }
}

function cancelEdit(row: any) {
  if (!row) return

  if (row.isNew) {
    rows.value = rows.value.filter(r => r?.id !== row.id)
  } else {
    const idx = rows.value.findIndex(r => r?.id === row.id)
    if (idx !== -1 && editingRow.value) {
      // Restore the original deep clone
      rows.value = rows.value.map((r, i) => i === idx ? JSON.parse(JSON.stringify(editingRow.value)) : r)
    } else {
      console.warn('cancelEdit: row not found or already removed', row)
    }
  }

  autoOpenSelectCell.value = null
  editingRowId.value = null
  editingRow.value = null
  isAddingRow.value = false
  validationErrors.value = {}
}

const resizingColumn = ref<ColumnDef | null>(null)
const startX = ref(0)
const startWidth = ref(0)
const tableRef = ref<HTMLElement | null>(null)

function bumpRowVersion(rowId: string | number | null | undefined) {
  if (rowId == null) return
  rowVersions[rowId] = (rowVersions[rowId] || 0) + 1
}

function cloneRow(row: any) {
  return JSON.parse(JSON.stringify(row))
}

function isPlainObject(value: any) {
  return value != null && typeof value === "object" && !Array.isArray(value)
}

function savedRowPayload(responseData: any) {
  if (!isPlainObject(responseData)) return {}
  if (isPlainObject(responseData.item)) return responseData.item
  if (isPlainObject(responseData.row)) return responseData.row
  if (isPlainObject(responseData.data)) return responseData.data
  // Several controllers return the saved model directly. Ignore message-only responses.
  return Object.keys(responseData).some((key) => key !== "message" && key !== "error") ? responseData : {}
}

function entityParamRoot(entity: string | null | undefined) {
  const name = String(entity || "").replace(/^\//, "").replace(/\.json$/, "").split("/")[0]
  const keepAsIs = new Set([
    "kunde",
    "rechtsgrundlagen",
    "pflichten",
    "rechtsgrundlagenmatrix",
    "pflichtenmatrix",
  ])
  if (keepAsIs.has(name)) return name
  return name.endsWith("s") ? name.slice(0, -1) : name
}

function savePayload(saveEntity: string, entries: [string, any][]) {
  const flat = Object.fromEntries(entries)
  if (Array.isArray(flat.iso_standards)) {
    flat.iso_standard_ids = flat.iso_standards
      .map((entry: any) => typeof entry === "object" && entry !== null ? entry.id : entry)
      .filter((id: any) => id != null)
    delete flat.iso_standards
  }
  const root = entityParamRoot(saveEntity)
  return { ...flat, [root]: flat }
}

function mergeLocalRow(rowId: string | number, optimisticRow: any, responseData: any = null, baseRow: any = null) {
  const serverRow = savedRowPayload(responseData)
  const hasServerRow = Object.keys(serverRow).length > 0
  rows.value = rows.value.map((r) => {
    if (r?.id !== rowId) return r
    return hasServerRow ? { ...(baseRow || r), ...serverRow } : { ...r, ...optimisticRow }
  })
  bumpRowVersion(rowId)
}

function restoreLocalRow(rowId: string | number, previousRow: any) {
  rows.value = rows.value.map((r) => r?.id === rowId ? cloneRow(previousRow) : r)
  bumpRowVersion(rowId)
}

function stripOptimisticState(row: any) {
  const clean = { ...(row || {}) }
  delete clean.__saving
  delete clean.__optimistic
  delete clean.isNew
  return clean
}

function snapshotRow(rowId: string | number) {
  const row = rows.value.find((r) => r?.id === rowId)
  return row ? cloneRow(row) : null
}

function setLocalRowSaving(rowId: string | number, saving = true) {
  rows.value = rows.value.map((r) => r?.id === rowId ? { ...r, __saving: saving } : r)
  bumpRowVersion(rowId)
}

function invalidateGefahrstoffePageCache() {
  if (props.endpoint === "gefahrstoffe") gefahrstoffePageCache.clear()
}

function optimisticReplaceRow(rowId: string | number, optimisticRow: any) {
  invalidateGefahrstoffePageCache()
  rows.value = rows.value.map((r) => {
    if (r?.id !== rowId) return r
    return { ...r, ...optimisticRow, id: rowId, __saving: true, __optimistic: true }
  })
  bumpRowVersion(rowId)
}

function prependTemporaryRow(row: any) {
  invalidateGefahrstoffePageCache()
  rows.value = [{ ...row, __saving: true, __optimistic: true }, ...rows.value]
  totalRows.value += 1
  dataVersion.value++
}

function replaceLocalRow(rowId: string | number, row: any) {
  invalidateGefahrstoffePageCache()
  const cleanRow = stripOptimisticState(row)
  const replacementId = cleanRow?.id ?? rowId
  let replaced = false

  rows.value = rows.value.map((r) => {
    if (r?.id !== rowId) return r
    replaced = true
    return cleanRow
  })

  if (!replaced) {
    rows.value = [cleanRow, ...rows.value]
    totalRows.value += 1
    dataVersion.value++
  }

  bumpRowVersion(rowId)
  bumpRowVersion(replacementId)
}

function patchLocalRowsByIds(rowIds: Array<string | number>, patch: Record<string, any>) {
  invalidateGefahrstoffePageCache()
  const wantedIds = new Set(rowIds.map((id) => String(id)))
  let changed = false

  rows.value = rows.value.map((row) => {
    if (!wantedIds.has(String(row?.id))) return row

    changed = true
    bumpRowVersion(row.id)
    return { ...row, ...patch }
  })

  if (changed) dataVersion.value++
}

function removeLocalRow(rowId: string | number) {
  invalidateGefahrstoffePageCache()
  const before = rows.value.length
  rows.value = rows.value.filter((r) => r?.id !== rowId)
  if (rows.value.length !== before) {
    totalRows.value = Math.max(0, totalRows.value - 1)
    dataVersion.value++
  }
}

function rollbackLocalRow(rowId: string | number, previousRow: any = null) {
  if (previousRow) {
    restoreLocalRow(rowId, previousRow)
  } else {
    removeLocalRow(rowId)
  }
}

function preserveOptionOrderForColumn(col: any, options: any[] | null = null) {
  return dropdownSortConfigForColumn(col, options).preserveOrder
}

function sortedOptionsForColumn(col: any, options: any[]) {
  return sortDropdownOptions(options || [], dropdownSortConfigForColumn(col, options))
}

function optionsForColumn(col: any) {
  return sortedOptionsForColumn(col, col?.options || dynamicOptions.value[col?.key] || [])
}

function normalizedConditionalControlValues(value: any) {
  if (isEmptyFilterValue(value)) return []
  return (Array.isArray(value) ? value : [value])
    .filter((item) => !isEmptyFilterValue(item))
    .map((item) => String(item))
}

function isConditionalFilterActiveForValue(col: any, controlValue: any) {
  const conditional = col?.conditional_filter
  if (!conditional) return false

  const values = normalizedConditionalControlValues(controlValue)
  if (conditional.active_when_empty && values.length === 0) return true
  return values.length === 1 && values[0] === String(conditional.equals)
}

function isConditionalFilterActive(col: any) {
  const conditional = col?.conditional_filter
  if (!conditional) return false
  return isConditionalFilterActiveForValue(
    col,
    serverColumnFilters.value[conditional.controlling_key]
  )
}

function clearIncompatibleConditionalDependentFilters(
  controllingKey: string,
  nextValue: any
) {
  for (const col of columns.value) {
    if (col?.conditional_filter?.controlling_key !== controllingKey) continue

    const selected = normalizedConditionalControlValues(serverColumnFilters.value[col.key])
    if (selected.length === 0) continue
    const allowedIds = new Set(
      filterOptionsForColumnAndControlValue(col, nextValue).map((option: any) => String(option.id))
    )
    if (selected.every((value) => allowedIds.has(value))) continue

    delete serverColumnFilters.value[col.key]
    delete selectedOptions.value[col.key]
  }
}

function reconcileConditionalFilterState() {
  let changed = false
  for (const col of columns.value) {
    const conditional = col?.conditional_filter
    if (!conditional) continue

    const selected = normalizedConditionalControlValues(serverColumnFilters.value[col.key])
    if (selected.length === 0) continue

    const allowedIds = new Set(filterOptionsForColumn(col).map((option: any) => String(option.id)))
    const shouldClear = !selected.every((value) => allowedIds.has(value))

    if (shouldClear) {
      delete serverColumnFilters.value[col.key]
      delete selectedOptions.value[col.key]
      changed = true
    }
  }
  return changed
}

function normalFilterOptionsForColumn(col: any) {
  const options =
    col?.display_options && col.display_options.length
      ? col.display_options
      : (col?.options || dynamicOptions.value[col?.key] || [])

  return sortedOptionsForColumn(col, options)
}

function filterOptionsForColumnAndControlValue(col: any, controlValue: any) {
  const conditional = col?.conditional_filter
  if (!conditional || !isConditionalFilterActiveForValue(col, controlValue)) {
    return normalFilterOptionsForColumn(col)
  }

  const conditionalOptions = conditional.options || []
  const controlValues = normalizedConditionalControlValues(controlValue)
  if (conditional.combine_with_normal_when_empty && controlValues.length === 0) {
    return [...normalFilterOptionsForColumn(col), ...conditionalOptions]
  }
  return conditionalOptions
}

function filterOptionsForColumn(col: any) {
  const conditional = col?.conditional_filter
  const controlValue = conditional
    ? serverColumnFilters.value[conditional.controlling_key]
    : undefined
  return filterOptionsForColumnAndControlValue(col, controlValue)
}

function isInlineSelectColumn(col: any) {
  return col?.type === "select" || col?.type === "multi-select" || col?.type === "many"
}

function isAutoOpenSelectCell(row: any, col: any) {
  return autoOpenSelectCell.value?.rowId === row?.id && autoOpenSelectCell.value?.colKey === col?.key
}

function canOpenInlineSelectCell(row: any, col: any) {
  return Boolean(
    props.endpoint &&
    props.enableEdit &&
    !props.alwaysEdit &&
    col?.write &&
    !col?.disabled &&
    row?.id != null &&
    isInlineSelectColumn(col)
  )
}

function openInlineSelectCell(row: any, col: any, event?: MouseEvent) {
  if (!canOpenInlineSelectCell(row, col)) return
  event?.stopPropagation()
  editingRowId.value = row.id
  editingRow.value = cloneRow(row)
  autoOpenSelectCell.value = { rowId: row.id, colKey: col.key }
}

async function updateSelectCell(row: any, col: any, value: any) {
  const shouldAutoSave = isAutoOpenSelectCell(row, col)
  setNested(row, col.key, value)
  const pairedPatch = pairedSelectPatch(col, value, optionsForColumn(col))
  Object.entries(pairedPatch).forEach(([key, pairedValue]) => setNested(row, key, pairedValue))
  if (!shouldAutoSave) return
  autoOpenSelectCell.value = null
  editingRowId.value = null
  await saveRow(row)
}

async function toggleManySelectCell(col: any, row: any, oid: number, val: any) {
  const shouldAutoSave = isAutoOpenSelectCell(row, col)
  toggleManyEdit(col, row, oid, val)
  if (!shouldAutoSave || col.paired_multi) return
  autoOpenSelectCell.value = null
  editingRowId.value = null
  await saveRow(row)
}

async function closeManySelectCell(col: any, row: any) {
  if (!col.paired_multi || !isAutoOpenSelectCell(row, col)) return
  autoOpenSelectCell.value = null
  editingRowId.value = null
  await saveRow(row)
}
const tableBodyMessage = computed(() => {
  if (isLoading.value) return ""
  if (loadErrorMessage.value) return loadErrorMessage.value
  if (rows.value.length === 0) return "Keine Daten"
  return ""
})

const columnCssVars = computed(() => {
  const style: Record<string, string> = {}
  for (const col of columns.value) {
    if (col.key !== 'actions') {
      style[columnCssVariableName(col.key, "width")] = `${displayColumnWidth(col)}px`
    }
  }
  Object.assign(style, stickyColumnCssVariables(
    visibleDataColumns.value,
    stickyColumnLeftByKey.value,
    isStickyColumnTable.value,
  ))
  return style
})

function startResize(e: MouseEvent, col: ColumnDef) {
  resizingColumn.value = col
  startX.value = e.clientX
  startWidth.value = displayColumnWidth(col)
  columnWidthOverrides[String(col.key)] = startWidth.value

  window.addEventListener('mousemove', onResize)
  window.addEventListener('mouseup', stopResize)
}

function onResize(e: MouseEvent) {
  if (!resizingColumn.value) return
  const delta = e.clientX - startX.value
  const width = Math.max(MIN_COLUMN_WIDTH, startWidth.value + delta)
  columnWidthOverrides[String(resizingColumn.value.key)] = width
  tableRef.value?.style.setProperty(
    columnCssVariableName(resizingColumn.value.key, "width"),
    `${width}px`
  )
}

async function stopResize() {
  const resizedColumn = resizingColumn.value
  if (resizedColumn) {
    const columnKey = String(resizedColumn.key)
    const requestedWidth = columnWidthOverrides[columnKey] ?? startWidth.value
    try {
      const response = await axios.post(`/${props.endpoint}/resizecol`, {
		idx: resizedColumn.sort,
        column: resizedColumn.key,
        width: requestedWidth
      })

      const savedWidth = Math.max(MIN_COLUMN_WIDTH, Number(response.data?.width || requestedWidth))
      setColumnWidth(props.columns, resizedColumn.key, savedWidth)
      setColumnWidth([resizedColumn], resizedColumn.key, savedWidth)
      columnWidthOverrides[columnKey] = savedWidth
      const persistenceKey = columnPersistenceKey.value
      if (persistenceKey) columnStore.setColumns(persistenceKey, columns.value)

      tableRef.value?.style.setProperty(columnCssVariableName(resizedColumn.key, "width"), `${savedWidth}px`)
	} catch (error: any) {
	  setColumnWidth([resizedColumn], resizedColumn.key, startWidth.value)
      columnWidthOverrides[columnKey] = startWidth.value
      tableRef.value?.style.setProperty(columnCssVariableName(resizedColumn.key, "width"), `${startWidth.value}px`)
	  const message =
		error.response?.data?.error ||
		error.response?.data?.message ||
		error.response?.statusText ||
		"Failed to update column width"
      toast({ title: "Error", description: message, variant: "destructive" })
    }
  }

  resizingColumn.value = null
  window.removeEventListener('mousemove', onResize)
  window.removeEventListener('mouseup', stopResize)
}


const totalPages = computed(() => {
  if (props.endpoint === "gefahrstoffe" && !totalRowsKnown.value) {
    return serverPage.value + (hasMoreRows.value ? 1 : 0)
  }
  return Math.max(1, Math.ceil(totalRows.value / serverPageSize.value))
})

function consumeGlobalSearchNavigation() {
  if (props.assignmentScopeArea === "dashboard") return false
  const key = "globalsearch-nav"
  const v = sessionStorage.getItem(key)
  if (v) {
    sessionStorage.removeItem(key)
    // A global-search result may remount a legal table whose last regular
    // sidebar navigation reset key is still non-zero. Treat that existing key
    // as consumed so this first request keeps session[:globalsearch]. A later
    // sidebar navigation increments the key and still performs its normal reset.
    consumedResetSessionFilterKey.value = props.resetSessionFilterKey ?? null
  }
  return Boolean(v)
}

function clearDatascopeFilter() {
  delete selectedOptions.value["datascope"]
  delete serverColumnFilters.value["datascope"]
}

const shouldForceEigeneDatascope = computed(() => {
  return shouldForceOwnDatascope(userInfo, props.endpoint)
})

function forceEigeneDatascopeFilter() {
  if (props.assignmentScopeArea === "dashboard") return
  if (applyPreferredDatascopeFilterToVisibleState()) {
    return
  }
  if (!shouldForceEigeneDatascope.value) return
  if (hasActivePersistedFilterState()) return
  // datascope is a multi-select filter; backend expects datascope[]=1 for "Eigene"
  selectedOptions.value["datascope"] = [1]
  serverColumnFilters.value["datascope"] = [1]
}

onMounted(async () => {
  if (props.endpoint === "gefahrstoffe") performance.mark("gefahrstoffe-component-mounted")
  if (consumeGlobalSearchNavigation()) {
    clearDatascopeFilter()
  } else {
    forceEigeneDatascopeFilter()
  }
  // Start the useful row request before secondary option requests. This keeps
  // option endpoints from occupying all browser connections ahead of the list.
  const rowsPromise = loadServerData()
  const optionsPromise = loadDynamicOptions()
  await Promise.allSettled([rowsPromise, optionsPromise])
})

watch(() => props.endpoint, async () => {
  if (consumeGlobalSearchNavigation()) {
    clearDatascopeFilter()
  } else {
    forceEigeneDatascopeFilter()
  }
  const rowsPromise = loadServerData()
  const optionsPromise = loadDynamicOptions()
  await Promise.allSettled([rowsPromise, optionsPromise])
})


let columnOrderBeforeDrag: string[] = []
let columnReorderSequence = 0
let activeColumnDragSequence: number | null = null
let columnReorderSaveQueue: Promise<unknown> = Promise.resolve()
const isColumnReordering = ref(false)

function onColumnReorderStart() {
  columnOrderBeforeDrag = columns.value.map((column: any) => String(column.key))
  activeColumnDragSequence = ++columnReorderSequence
  if (isGefahrstoffeTable.value) isColumnReordering.value = true
}

function restoreOrderFromEvent(evt: any) {
  const current = columns.value.map((column: any) => String(column.key))
  return restoreColumnOrderFromMove(current, evt?.oldIndex, evt?.newIndex)
}

function finishColumnReorder(sequence: number) {
  if (sequence !== columnReorderSequence) return
  // Keep the already-painted indicator visible until the final table state has
  // reached the screen, including the heavier failure rollback path.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (sequence === columnReorderSequence) isColumnReordering.value = false
  }))
}

function persistOptimisticColumnOrder(sequence: number, orderedKeys: string[], previousKeys: string[]) {
  if (sequence !== columnReorderSequence) return
  const persistenceKey = columnPersistenceKey.value
  if (persistenceKey) columnStore.setColumns(persistenceKey, columns.value)
  if (isGefahrstoffeTable.value) {
    performance.mark("gefahrstoffe-column-order-painted")
    performance.measure(
      "gefahrstoffe-column-optimistic-render",
      "gefahrstoffe-column-drop",
      "gefahrstoffe-column-order-painted"
    )
  }

  const saveRequest = columnReorderSaveQueue.then(() => (
    axios.post(`/${props.endpoint}/swapcols`, { orderedKeys })
  ))
  columnReorderSaveQueue = saveRequest.catch(() => undefined)

  void saveRequest.then(() => {
    if (sequence !== columnReorderSequence) return
    if (isGefahrstoffeTable.value) performance.mark("gefahrstoffe-column-order-persisted")
    finishColumnReorder(sequence)
  }).catch((error: any) => {
    if (sequence !== columnReorderSequence) return
    optimisticColumnOrder.value = previousKeys
    if (persistenceKey) columnStore.setColumns(persistenceKey, columns.value)
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.statusText ||
      "Failed to save column order."
    toast({ title: "Error", description: message, variant: "destructive" })
    finishColumnReorder(sequence)
  })
}

const onColumnReorder = (evt: any) => {
  const previousKeys = columnOrderBeforeDrag.length > 0
    ? [...columnOrderBeforeDrag]
    : restoreOrderFromEvent(evt)
  columnOrderBeforeDrag = []
  const orderedKeys = columns.value
    .filter(c => c.show && c.key !== 'actions' && c.key !== 'buffer')
    .map(c => c.key)

  const sequence = activeColumnDragSequence ?? ++columnReorderSequence
  activeColumnDragSequence = null
  if (isGefahrstoffeTable.value) performance.mark("gefahrstoffe-column-drop")
  // Two frames guarantee that Vue's optimistic header/body order reaches the
  // screen before Pinia persistence and the network request begin.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    persistOptimisticColumnOrder(sequence, orderedKeys, previousKeys)
  }))
}

function formatISOForDateInput(val: string | undefined | null) {
  if (!val) return ''
  // If val contains a timestamp, strip it
  return val.split('T')[0]
}

const selectedOptions = ref<Record<string, any[]>>({})

// Pre-populate filters from column specs that carry a defaultFilter (e.g. datascope → [1] "Eigene").
// Only apply once per session/endpoint — after the user changes the filter, their choice is kept.
const defaultFilterAppliedKey = `defaultFilter-applied-${props.endpoint}`
const restoredPersistedFilters = loadPersistedFilterState()
if (!restoredPersistedFilters && !sessionStorage.getItem(defaultFilterAppliedKey)) {
  for (const col of props.columns) {
    if (col.defaultFilter && Array.isArray(col.defaultFilter) && col.defaultFilter.length > 0) {
      selectedOptions.value[col.key] = [...col.defaultFilter]
      serverColumnFilters.value[col.key] = [...col.defaultFilter]
    }
  }
  sessionStorage.setItem(defaultFilterAppliedKey, "1")
}
applyPreferredDatascopeFilterToVisibleState()

function clearVisibleFilterStateForSessionReset() {
  clearServerFilterStateForSessionReset()
  selectedOptions.value = {}
  forceEigeneDatascopeFilter()
}

watch(
  () => props.columns.filter(col => col.disabled).map(col => col.key).sort().join("|"),
  () => {
    clearDisabledColumnFilters()
    if (props.endpoint) loadServerData()
  }
)

watch(
  profileAssignmentScopeMode,
  (mode, previousMode) => {
    if (!mode || mode === previousMode) return
    if (hasSessionDatascopeOverride()) return
    if (!applyProfileDatascopeFilterToVisibleState()) return
    serverPage.value = 1
    loadServerData()
  }
)

watch(
  () => props.resetSessionFilterKey,
  () => {
    if (props.endpoint && !props.data && pendingResetSessionFilterKey() !== null) {
      if (props.filterStateKey && loadPersistedFilterState()) {
        serverPage.value = 1
      } else {
        clearVisibleFilterStateForSessionReset()
      }
      loadServerData()
    }
  }
)

function toggleManyEdit(col, row, oid, val) {
  if (col.disabled) return
	let k = col.key;
	let currentValue = getNested(row, k)
	if (col.paired_multi) {
	  const patch = pairedMultiSelectPatch(col, currentValue, oid, val, optionsForColumn(col))
	  setNested(row, k, patch.value)
	  Object.entries(patch.paired).forEach(([key, pairedValue]) => setNested(row, key, pairedValue))
	  rowVersions[row.id] = (rowVersions[row.id] || 0) + 1
	  return
	}
	if (!Array.isArray(currentValue)) {
	  currentValue = toCollection(currentValue)
	}

	if (val) {
	  let missingOption = optionsForColumn(col).find(opt => opt.id == oid);
	  if (!missingOption) return
	  const exists = currentValue.some(item => {
	    const itemId = typeof item === 'object' && item !== null ? item.id : item
	    return itemId == oid
	  })
	  if (!exists) setNested(row, k, [...currentValue, {...missingOption}]);
	} else {
	  setNested(row, k, currentValue.filter(item => {
	    const itemId = typeof item === 'object' && item !== null ? item.id : item
	    return itemId != oid
	  }));
	}
	rowVersions[row.id] = (rowVersions[row.id] || 0) + 1
}

function toggleManyFilter(col, optionId, checked) {
	let colKey = col.key;
	if (!selectedOptions.value[colKey]) selectedOptions.value[colKey] = []
	if (colKey === "datascope") {
	  const optionValue = Number(optionId)
	  if (checked && (optionValue === 0 || optionValue === 1)) {
	    selectedOptions.value[colKey] = [optionValue]
	  } else if (checked) {
	    selectedOptions.value[colKey] = selectedOptions.value[colKey].filter(id => Number(id) !== 0 && Number(id) !== 1)
	    if (!selectedOptions.value[colKey].some(id => Number(id) === optionValue)) {
	      selectedOptions.value[colKey].push(optionValue)
	    }
	  } else {
	    selectedOptions.value[colKey] = selectedOptions.value[colKey].filter(id => Number(id) !== optionValue)
	  }
	  onColumnFilterChange(colKey, selectedOptions.value[colKey])
	  return
	}
	if (checked) {
	  if (!selectedOptions.value[colKey].includes(optionId))
	    selectedOptions.value[colKey].push(optionId)
	} else {
	  selectedOptions.value[colKey] = selectedOptions.value[colKey].filter(id => id !== optionId)
	}
    onColumnFilterChange(colKey, selectedOptions.value[colKey])
}

function toSelectHash(options, arr, displayOptions = null) {
	console.log("JSON.stringify(options)=" + JSON.stringify(options));
	console.log("JSON.stringify(arr)=" + JSON.stringify(arr));
	if (arr == null || arr == "") {
		return {}
	}
	  return toCollection(arr).reduce((acc, obj) => {
	    const id = typeof obj === 'object' && obj !== null ? obj.id : obj
	    acc[id] = optionLabel(options, { id }, displayOptions);
	    return acc;
	  }, {});
}

function toFilterHash(options, arr, displayOptions = null) {
	if (arr == null) {
	    return [];
	} 
	
	console.log("arr=" + JSON.stringify(arr))
	return toSelectHash(options, arr, displayOptions)
}

function toCollection(list) {
  if (list == null) {
	return []
  }
  if (Array.isArray(list)) return list
  // ManySelect / view rendering expects an array of objects like `{ id }`.
  // Some backends store a single scalar (e.g. datascope = 0/1). Normalize it.
	if (typeof list !== "object") {
	return [{ id: list }]
  }
  return Object.values(list)
}

function selectCellText(row: any, col: any) {
  const value = getNested(row, col.key)
  if (value === null || value === undefined || value === "") return ""
  const values = Array.isArray(value) ? value : [value]
  return values.map((entry: any) => {
    const item = typeof entry === "object" && entry !== null ? entry : { id: entry }
    return optionLabel(optionsForColumn(col), item, col.display_options)
  }).filter(Boolean).join(", ")
}

function refreshDatascopeFilterOptions() {
  const col = columns.value.find(c => c.key === "datascope")
  if (!col || (col.type !== "many" && col.type !== "multi-select")) return

  const specCol = props.columns.find(c => c.key === "datascope")
  const baseOptions =
    Array.isArray(specCol?.options) && specCol.options.length > 0
      ? specCol.options
      : (Array.isArray(col.options) ? col.options : [])

  const byId = new Map<any, { id: any; label: string }>()
  byId.set(-1, { id: -1, label: "Keine" })

  baseOptions.forEach((opt: any) => {
    if (opt?.id == null) return
    if (!byId.has(opt.id)) byId.set(opt.id, { ...opt, label: String(opt.label) })
  })

  rows.value.forEach((row) => {
    const values = toCollection(getNested(row, "datascope"))
    values.forEach((entry: any) => {
      const id = typeof entry === "object" ? entry.id : entry
      if (id == null) return
      const fallbackLabel = optionLabel(baseOptions, { id }, col.display_options)
      const label = (typeof entry === "object" && entry.label) ? entry.label : String(fallbackLabel)
      if (!byId.has(id)) byId.set(id, { id, label })
    })
  })

  // Keep the forced default "Eigene" option visible in the filter even when
  // the current page rows do not contain any datascope entry with id=1.
  if (shouldForceEigeneDatascope.value && !byId.has(1)) {
    const ownLabel = String(optionLabel(baseOptions, { id: 1, label: t('view.own') }, col.display_options))
    byId.set(1, { id: 1, label: ownLabel || t('view.own') })
  }

  col.options = sortedOptionsForColumn(col, Array.from(byId.values()))
}

const entityStore = useEntityStore()
const { currentItem, showEditor, editorSaving } = storeToRefs(entityStore)

function saveEditorOptimistically() {
  const entity = props.endpoint
  if (!entity) return

  const item = currentItem.value?.[entity]
  const previousRow = hasEntityId(item)
    ? cloneRow(rows.value.find((r) => r?.id === item.id) || item)
    : null

  entityStore.saveEditor(
    entity,
    props.saveendpoint || entity,
    (savedItem, responseData) => {
      if (hasEntityId(savedItem)) {
        mergeLocalRow(savedItem.id, savedItem, responseData, previousRow)
      } else {
        loadServerData()
      }
      emit('data-mutated', entity, responseData)
    },
    (k, v) => filterWriteable(k, v),
    (optimisticItem) => {
      if (hasEntityId(optimisticItem)) mergeLocalRow(optimisticItem.id, optimisticItem)
    },
    (optimisticItem) => {
      if (hasEntityId(optimisticItem) && previousRow) restoreLocalRow(optimisticItem.id, previousRow)
    }
  )
}

function currentQueryParams() {
  return { ...buildQueryParams() }
}

function currentExportColumns() {
  return columns.value
    .filter((col: any) => col.key !== 'actions' && col.show !== false)
    .map((col: any) => ({
      key: col.key,
      label: col.label,
      type: col.type,
    }))
}

defineExpose({
  onColumnFilterChange,
  reloadData: loadServerData,
  currentQueryParams,
  currentExportColumns,
  snapshotRow,
  setLocalRowSaving,
  optimisticReplaceRow,
  prependTemporaryRow,
  replaceLocalRow,
  patchLocalRowsByIds,
  rollbackLocalRow,
  removeLocalRow,
});

function onToggleColumn(
  change: ColumnPresentationChange = "visibility",
  stickyColumn?: { key: string; sticky: number },
) {
	// Sticky changes must remain paint-first: avoid synchronous Pinia/localStorage
	// persistence before the browser can render the optimistic pin state.
	if (change === "sticky") {
    if (stickyColumn) stickyColumnOverrides[stickyColumn.key] = stickyColumn.sticky
    return
  }

	// Visibility changes still use local presentation persistence.
	const persistenceKey = columnPersistenceKey.value
	if (persistenceKey) columnStore.setColumns(persistenceKey, columns.value)
	columnToggleSeq.value++
  const requiresDataReload = columnChangeRequiresDataReload(change)
  if (props.endpoint === "gefahrstoffe" && requiresDataReload) {
    gefahrstoffePageCache.clear()
    void loadServerData()
  }
  if (requiresDataReload) void loadDynamicOptions()
}

onUnmounted(() => {
  activeLoadAbortController?.abort()
  activeCountAbortController?.abort()
})

const menuVisible = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const selectedCellRow = ref('')
const selectedCellCol = ref('')
const selectedCellContent = ref('')
const hoverCopyMenuItem = ref(false)
const hoverEmailMenuItem = ref(false)

function showContextMenu(event: MouseEvent, row : Object, col : Object, content: string) {
  if (columns.value.find(column => column.key === col)?.disabled) return

  console.log("showContextMenu row=" + row + " col=" + col + " content=" + content)
  // Only trigger for secondary button (right click)
  if (event.button !== 2) return
  event.preventDefault()
  event.stopPropagation()

  selectedCellRow.value = row
  selectedCellCol.value = col
  selectedCellContent.value = content

  // Prevent menu from going outside the viewport
  const menuWidth = 140
  const menuHeight = 36
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  menuX.value = event.clientX + menuWidth > viewportWidth ? viewportWidth - menuWidth - 10 : event.clientX
  menuY.value = event.clientY + menuHeight > viewportHeight ? viewportHeight - menuHeight - 10 : event.clientY

  menuVisible.value = true
}

function hideContextMenu() {
  menuVisible.value = false
}

function copyToClipboard() {
  navigator.clipboard.writeText(selectedCellContent.value).then(() => {
    toast({ title: "Copied", description: selectedCellContent.value })
    hideContextMenu()
  })
}

function notifyStatus() {

  emit("cell-notified", {
    id: selectedCellRow.value,
    path: selectedCellCol.value,
    value: selectedCellContent.value
  });
}

// Hide menu when clicking anywhere else
onMounted(() => window.addEventListener('click', hideContextMenu))
onUnmounted(() => window.removeEventListener('click', hideContextMenu))

// Computed property for add button text with correct word order
const addButtonText = computed(() => {
  const itemLabel = translateLabel(props.labelSingular || props.label)
  const addLabel = t('view.add')
  // English: "Add [Item]", German/Chinese: "[Item] hinzufügen/添加"
  return locale.value === 'en' ? `${addLabel.charAt(0).toUpperCase() + addLabel.slice(1)} ${itemLabel}` : `${itemLabel} ${addLabel}`
})


</script>

<template>
  <Card  :key="stableKey" class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex justify-between items-center p-4 bg-gray-50 border-b h-[65px]">
		<h2 class="text-xl font-bold flex items-center gap-2">
		  <slot name="logo" />
		  <span>{{ translateLabel(props.label)}}</span>
		</h2>
		<div v-if="props.enableColFilterTop" class="shrink-0 ml-4">
            <ColumnSelect :columns="columns" :endpoint="endpoint" @toggleColumn="onToggleColumn" :enableAll="enableAll" :ownColumnKeys="props.ownColumnKeys" />
		</div>
		<Toaster />
	  <div class="flex gap-2 items-center w-full ml-4">
		<slot name="extraglobalactions" />		
        <Input v-if="enableSearch" v-model="serverSearch" placeholder="Search..." @input="onSearchChange(($event.target as HTMLInputElement).value)" class="w-48" />
        <Button v-if="enableAdd" @click="addRow" class="greenbutton text-white">{{ addButtonText }}</Button>
      </div>
    </div>

    <!-- Table container -->
    <div ref="tableRef" class="flex-1 overflow-auto overflow-x-scroll relative" :style="columnCssVars" :aria-busy="isLoading || isRefreshing || isColumnReordering ? 'true' : 'false'">
      <div
        v-if="isGefahrstoffeTable && isColumnReordering"
        class="pointer-events-none fixed right-6 top-24 z-[1700] flex w-max items-center gap-2 rounded-md bg-slate-900/90 px-3 py-2 text-sm text-white shadow-lg"
        role="status"
        aria-live="polite"
      >
        <Loader2 class="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>Spalten werden angeordnet…</span>
      </div>
      <div
        v-if="refreshWarningMessage"
        class="sticky top-0 left-0 z-[1600] bg-amber-50 px-3 py-2 text-xs text-amber-800"
      >
        {{ refreshWarningMessage }}
      </div>
      <div
        v-else-if="isRefreshing"
        class="absolute right-3 top-3 z-[1600] rounded bg-white/90 px-2 py-1 text-xs text-gray-500 shadow"
      >
        Aktualisierung…
      </div>
      <div
        v-if="isLoading && !isSortLoading"
        class="absolute left-0 right-0 bottom-0 top-[96px] z-[1500] flex items-center justify-center bg-white/60"
      >
        <div class="flex items-center gap-3 px-4 py-2 rounded-md bg-white shadow">
          <div class="datatable-spinner" aria-label="Loading" />
          <span class="text-sm text-gray-700">{{ t('view.loading') || 'Loading…' }}</span>
        </div>
      </div>
      <table
        class="border-collapse"
        :class="enforcesConfiguredWidths ? 'table-fixed' : 'min-w-max table-auto'"
        :style="dataTableStyle"
      >
		<colgroup v-if="enforcesConfiguredWidths || isStickyColumnTable">
		  <col
		    v-if="!props.hideActionColumn"
		    key="actions"
		    :style="{ width: actionColumnWidthPx + 'px' }"
		  />
		  <col
		    v-for="col in visibleDataColumns"
		    :key="col.key"
		    :style="{ width: displayColumnWidth(col) + 'px', minWidth: displayColumnWidth(col) + 'px', maxWidth: displayColumnWidth(col) + 'px' }"
		  />
		</colgroup>
		<thead>
		  <!-- Header row with draggable -->
		  <draggable
		    v-model="columns"
		    item-key="key"
		    tag="tr"
		    :animation="200"
		    :handle="'.drag-handle'"
		    :filter="'.fixed-column'"
		    @start="onColumnReorderStart"
		    @end="onColumnReorder"
		  >
			    <template #item="{ element }">
			      <th
			        v-show="element.show !== false || (element.key === 'actions')"
			        class="sticky top-0 z-30 bg-gray-100 p-2 h-14 select-none border border-gray-200 relative"
				  :class="[
				    element.key === 'actions' ? 'left-0 z-1000 fixed-column' : '',
				    { 'datatable-column-disabled': element.disabled }
				  ]"
				  :style="element.key === 'actions'
				    ? actionColumnStyle('header')
				    : { width: `var(${columnCssVariableName(element.key, 'width')})`, minWidth: `var(${columnCssVariableName(element.key, 'width')})`, maxWidth: `var(${columnCssVariableName(element.key, 'width')})`, zIndex: isStickyDataColumn(element) ? 1001 : 999, fontSize: props.headerFontSize ? props.headerFontSize + 'px' : undefined, ...stickyDataCellStyle(element, 'header')}"
				>

		        <!-- Only render content if column is visible or it's Actions -->
		        <div class="flex items-center justify-between relative">
		          <div v-if="element.key === 'actions'" class="font-semibold">
		            {{ t('view.actions') }}
		          </div>
				  <!-- <div v-else-if="element.key === 'datascope'" class="font-semibold">
				    {{ element.label }}
				  </div> -->
				  <div v-else-if="element.key === 'buffer'" class="font-semibold">
				  </div>
		          <div v-else class="flex items-center justify-between relative w-full gap-2">
					<button
					  type="button"
					  class="text-left flex-1 th-label"
					  @click="sortBy(element.key)"
					  :title="element.disabledReason || element.label"
                      :disabled="element.disabled"
                      :class="{ 'text-gray-400 cursor-not-allowed': element.disabled }"
					  :style="{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }"
					>
					  {{ element.label }}
					  <span v-if="serverSortKey === element.key" class="ml-1 text-xs text-gray-500">
					    <span
					      v-if="isSortLoading"
					      class="inline-block w-3.5 h-3.5 align-middle"
					      style="border: 2px solid rgba(0,0,0,0.15); border-top-color: rgba(0,0,0,0.55); border-radius: 50%; animation: datatable-spin 0.8s linear infinite;"
					      aria-label="Sortierung wird geladen"
					    ></span>
					    <span v-else>{{ serverSortAsc ? "▲" : "▼" }}</span>
					  </span>
					</button>

		            <!-- Drag handle -->
		            <span v-if="element.key !== 'actions' && enableColChange" class="drag-handle ml-2 cursor-move px-2 py-1" :title="t('view.draghandle')">≡</span>

		            <!-- Resize handle -->
		            <div
		              v-if="element.key !== 'actions' && enableColChange"
		              class="absolute top-0 right-0 h-full cursor-col-resize z-20 flex items-center justify-center"
		              style="width: 8px;" 
		              @mousedown.prevent="startResize($event, element)"
		            >
		              <div class="w-0.5 h-6 bg-gray-500 rounded" :title="t('view.resizehandle')"></div>
		            </div>

					<slot name="colextensions" :col="element" />

		          </div>
		        </div>
		      </th>
		    </template>
			<tr class="w-full"/>
		  </draggable>

		  <!-- Filter row -->
		  <tr>
		    <th
		      v-for="col in visibleColumns"
		      :key="col.key"
		      class="sticky top-12 z-30 bg-gray-50 p-1 h-12 border border-gray-200"
		      :class="[
		        col.key === 'actions' ? 'left-0' : 'z-[999]',
		        { 'datatable-column-disabled': col.disabled }
		      ]"
		      :style="col.key === 'actions'
		        ? actionColumnStyle('filter')
		        : { width: `var(${columnCssVariableName(col.key, 'width')})`, minWidth: `var(${columnCssVariableName(col.key, 'width')})`, maxWidth: `var(${columnCssVariableName(col.key, 'width')})`, ...stickyDataCellStyle(col, 'filter')}"
			  :att="col.key"
		    >
		      <!-- Actions column filter cell -->
		      <template v-if="col.key === 'actions' && props.enableColFilterSide">
				<div class="flex items-center justify-center gap-2 h-full">
				<ColumnSelect :columns="columns" :endpoint="props.endpoint"  @toggleColumn="onToggleColumn" :enableAll="enableAll" :ownColumnKeys="props.ownColumnKeys"/>
				
				<Button v-if="props.enableFilter" :class="getFilterButtonColor()" variant="outline" size="sm" @click="resetFilters" :title="getFilterButtonText()">
				  <XCircle/>
				</Button>
                </div>
		      </template>

		      <!-- Normal filter cells -->
			  			  
			  <span v-if="col.disabled" class="block text-xs font-normal text-gray-500 cursor-not-allowed" :title="col.disabledReason" aria-disabled="true">{{ col.disabledReason }}</span>
              <template v-else-if="(col.type === 'multi-select' || col.type === 'many') && props.enableFilter">
				<ManySelect :col="{ ...col, options: filterOptionsForColumn(col), preserveOptionOrder: isConditionalFilterActive(col) }"
					            :selectedOptions="toFilterHash(filterOptionsForColumn(col), selectedOptions[col.key], col.display_options)"
							:dynamicOptions="dynamicOptions"
							:allowDisabledSelection="true"
				            :toggleOption="(oid, val) => toggleManyFilter(col, oid, val)" />
			  </template>

			  <template v-else-if="col.type === 'select' && (col.options || dynamicOptions[col.key])  && props.enableFilter">
			    <select
			      class="border rounded p-1 text-sm w-full"
			      @change="onColumnFilterChange(col.key, $event.target.value)"
			    >
			      <option value="">{{ t('view.all') }}</option>
			      <option
			        v-for="opt in filterOptionsForColumn(col)"
			        :key="opt.id"
			        :value="opt.id"
			      >
			        {{ opt.label }}
			      </option>
			    </select>
			  </template>

			  <!-- Boolean / checkbox filter: use a select with All/Yes/No -->
			  <template v-else-if="(col.type === 'boolean' || col.edit === 'checkbox') && props.enableFilter">
			    <select
			      class="border rounded p-1 text-sm w-full"
			      @change="onColumnFilterChange(col.key, $event.target.value)"
			    >
			      <option value="">{{ t('view.all') }}</option>
			      <option value="1">Ja</option>
			      <option value="0">Nein</option>
			    </select>
			  </template>

			  <!-- File columns: do not show a filter input -->
			  <template v-else-if="(col.type === 'file' || col.edit === 'file') && props.enableFilter">
			    <!-- No filter for file-type columns -->
			  </template>

			  <template v-else-if="col.key !== 'actions' && col.key !== 'buffer'  && props.enableFilter">
			    <Input
			      v-model="serverColumnFilters[col.key]"
			      @input="onColumnFilterChange(col.key, $event.target.value)"
			      :placeholder="col.type === 'date' ? 'i.e. >01.01.2025' : t('view.filter_placeholder')"
			      class="w-full text-sm"
			    />
			  </template>

		    </th>
		  </tr>
		</thead>

		<tbody>
		  <template v-if="!isLoading && tableBodyMessage">
		    <tr>
		      <td
		        :colspan="Math.max(visibleColumns.length, 1)"
		        class="sticky left-0 border border-gray-200 p-4 text-left text-gray-600 bg-white z-[998]"
		      >
		        {{ tableBodyMessage }}
		      </td>
		    </tr>
		  </template>
		  <template v-else>
		    <tr
		      v-for="(row, rowIndex) in rows"
		      :key="row.id"
          v-memo="[editingRowId === row.id, row.id, row.__saving, isRowDeleting(row.id), columnOrderKey, columnToggleSeq, dataVersion, rowVersions[row.id]]"
		      class="transition-colors odd:bg-white even:bg-gray-50 hover:bg-blue-50"
		      :class="{ 'opacity-60': row.__saving || isRowDeleting(row.id) }"
		    >
		    <!-- Actions cell -->
		    <td v-if="!props.hideActionColumn"
		      class="sticky left-0 p-1 border border-gray-200"
		      :style="actionColumnStyle('body')"
		    >
		      <div class="flex gap-2 justify-center">
		        <template v-if="isRowDeleting(row.id)">
		          <Loader2 class="w-4 h-4 animate-spin text-red-600" aria-label="Wird gelöscht" />
		        </template>
		        <template v-else-if="row.__saving">
		          <Save class="w-4 h-4 text-green-600 animate-pulse" />
		        </template>
		        <template v-else-if="editingRowId === row.id">
		          <Button :title="t('view.btn_save')" size="icon" variant="ghost" class="text-green-600 hover:bg-green-50" @click="saveRow(row)">
		            <Save class="w-4 h-4" />
		          </Button>
		          <Button :title="t('view.btn_cancel')" size="icon" variant="ghost" class="text-gray-600 hover:bg-gray-50" @click="cancelEdit(row)">
		            <X class="w-4 h-4" />
		          </Button>
		        </template>
		        <template v-else>
				  <slot name="extraactions" :row="row" :rowId="row.id"/>
		          <Button v-if="enableEdit" :title="t('view.btn_edit')" size="icon" variant="ghost" class="text-black-600 hover:bg-blue-50" @click="editRow(row)">
		            <Pencil class="w-4 h-4" />
		          </Button>
				  <Button v-if="enableDetail" :title="t('view.btn_detail')" size="icon" variant="ghost" class="text-black-600 hover:bg-blue-50" @click="entityStore.openEditor(props.endpoint, row)">
				    <Glasses class="w-4 h-4" />
				  </Button>
			      <Button v-if="enableDelete" :title="t('view.btn_delete')" size="icon" variant="ghost" class="text-red-600 hover:bg-red-50" @click="deleteRow(row.id)">
			        <Trash class="w-4 h-4" />
			      </Button>
		        </template>
		      </div>
		    </td>

		    <!-- Data cells -->
		    <TableCell
		      v-for="col in visibleDataColumns"
		      :key="col.key"
		      class="align-middle border border-gray-200 px-2 h-[60px] max-h-[60px] min-h-[60px]"
		      :class="{
		        'text-right font-mono': col.type === 'number',
		        'text-center': col.type === 'boolean' || col.edit === 'checkbox',
		        'bg-yellow-50': (editingRowId === row.id && col.write) || row[row.hitMap?.[col.key]] === 1,
		        'datatable-column-disabled': col.disabled
		      }"
              :style="bodyDataCellStyle(col, rowIndex, (editingRowId === row.id && col.write) || row[row.hitMap?.[col.key]] === 1)"
		    >
		      <!-- Edit mode -->
		      <div v-if="col.disabled" class="text-gray-400 cursor-not-allowed" :title="col.disabledReason" aria-disabled="true">—</div>
              <div v-else-if="(editingRowId === row.id || props.alwaysEdit) && col.write"
			  @contextmenu.prevent="showContextMenu($event, row.id, col.key, getNested(row, col.key))">
		        <template v-if="!col.type || (col.type === 'text' && col.edit !== 'richtext')">
		          <Input
		            :modelValue="getNested(row, col.key)"
		            @update:modelValue="val => setNested(row, col.key, val)"
		          />
		        </template>
				<template v-else-if="!col.type || (col.type === 'text' && col.edit === 'richtext')">
				  <div class="tiptap-cell">
				    <TiptapEditor
				      :model-value="getNested(row, col.key)"
				      @update:model-value="val => setNested(row, col.key, val)"
				    />
				  </div>
			    </template>
		        <template v-else-if="col.type === 'number'">
		          <input
		            type="number"
		            :value="getNested(row, col.key)"
		            @input="setNested(row, col.key, $event.target.valueAsNumber)"
		            class="border rounded p-1 text-sm w-full"
		          />
		        </template>

		        <template v-else-if="col.type === 'color'">
		          <input
		            type="color"
		            :value="getNested(row, col.key)"
		            @input="setNested(row, col.key, $event.target.value)"
		            class="h-8 w-12 p-0 border-none bg-transparent"
		          />
		        </template>

				<template v-else-if="col.type === 'date'">
					<input
					  type="date"
					  :value="formatISOForDateInput(getNested(row, col.key))"
					  @input="setNested(row, col.key, $event.target.value)"
					  class="border rounded p-1 text-sm w-full"
					/>
				</template>
		        <template v-else-if="col.type === 'boolean' || col.edit === 'checkbox'">
		          <input
		            type="checkbox"
		            :checked="isBooleanValue(getNested(row, col.key))"
		            @click="onCheckboxChange($event, row, col.key)"
		          />
		        </template>

		        <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
					<SingleSelect
					  :autoOpen="isAutoOpenSelectCell(row, col)"
					  :fontSize="props.singleSelectFontsize ? props.singleSelectFontsize : undefined"
					  :options="optionsForColumn(col)"
					  :displayOptions="col.display_options"
					  :preserveOrder="preserveOptionOrderForColumn(col)"
					  :modelValue="getNested(row, col.key)"
					  @update:modelValue="val => updateSelectCell(row, col, val)"
					/>
		        </template>
				
				<template v-else-if="col.type === 'many'">
				    <ManySelect  :col="col"
					             :autoOpen="isAutoOpenSelectCell(row, col)"
					             :selectedOptions="toSelectHash(optionsForColumn(col), getNested(row, col.key), col.display_options)"
								 :dynamicOptions="dynamicOptions"
								 :toggleOption="(oid, val) => toggleManySelectCell(col, row, oid, val)"
								 @closed="closeManySelectCell(col, row)"	/>
				</template>
				

		        <p v-if="validationErrors[col.key]" class="text-xs text-red-500 mt-1">
		          {{ validationErrors[col.key] }}
		        </p>
		      </div>
		      <!-- View mode -->
		      <div v-else
			  :class="{ 'cursor-pointer': canOpenInlineSelectCell(row, col) }"
			  @click="openInlineSelectCell(row, col, $event)"
			  @contextmenu.prevent="showContextMenu($event, row.id, col.key, getNested(row, col.key))">
		        <template v-if="col.type === 'boolean' || col.edit === 'checkbox'">
		          <div class="flex items-center justify-center">
		            <Check
		              v-if="isBooleanValue(getNested(row, col.key))"
		              class="w-5 h-5 text-green-600"
		            />
		            <X
		              v-else
		              class="w-5 h-5 text-red-600"
		            />
		          </div>
		        </template>
				<template v-else-if="col.type === 'text' && col.edit === 'richtext'">
					<Tooltip>
		            <TooltipTrigger>
		              <div class="truncate" :style="{ maxWidth: displayColumnWidth(col) + 'px' }">
					    <div class="richtext" v-html="row.content"></div>
		              </div>
		            </TooltipTrigger>
		            <TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
		              <div v-html="row.content"></div>
		            </TooltipContent>
		          </Tooltip>

				</template>

				
		        <template v-else-if="props.endpoint === 'gefahrstoffe' && col.key === 'standort_id'">
		          <div class="truncate" :style="{ maxWidth: displayColumnWidth(col) + 'px' }" :title="row.standort || ''">
		            {{ row.standort || '' }}
		          </div>
		        </template>

		        <template v-else-if="props.endpoint === 'gefahrstoffe' && (col.type === 'select' || col.type === 'multi-select')">
					<div
					  class="truncate"
					  :style="{ maxWidth: displayColumnWidth(col) + 'px' }"
					  :title="selectCellText(row, col)"
					>
					  {{ selectCellText(row, col) }}
					</div>
		        </template>

		        <template v-else-if="col.type === 'select'  || col.type === 'multi-select'">
					<SingleSelect :disabled="true"
					  :showNothing="!col.write && isAddingRow"
					  :fontSize="props.singleSelectFontsize ? props.singleSelectFontsize : undefined"
					  :options="optionsForColumn(col)"
					  :displayOptions="col.display_options"
					  :preserveOrder="preserveOptionOrderForColumn(col)"
					  :modelValue="getNested(row, col.key)"
					  @update:modelValue="val => setNested(row, col.key, val)"
					/>
		        </template>

				<template v-else-if="col.type === 'many'">
					<!-- Special handling for gefahrstoffe h_phrases -->
					<template v-if="props.endpoint === 'gefahrstoffe' && col.key === 'h_phrases'">
						<Tooltip>
							<TooltipTrigger>
								<div class="text-xs" :style="{ maxWidth: displayColumnWidth(col) + 'px' }">
									<div v-for="hp in toCollection(getNested(row, col.key))" :key="hp.id" class="mb-1">
										<strong>{{ hp.h_code }}</strong> {{ hp.h_phrase }}
									</div>
								</div>
							</TooltipTrigger>
							<TooltipContent class="whitespace-pre-line text-xs max-w-[500px] break-words">
								<div v-for="hp in toCollection(getNested(row, col.key))" :key="hp.id" class="mb-2">
									<strong>{{ hp.h_code }}:</strong> {{ hp.h_phrase }}
								</div>
							</TooltipContent>
						</Tooltip>
					</template>
					<!-- Special handling for gefahrstoffe ghs_pictograms: show images, filter/edit stays "many" -->
					<template v-else-if="props.endpoint === 'gefahrstoffe' && col.key === 'ghs_pictograms'">
			          <div class="flex items-center justify-center gap-1 flex-wrap">
			            <template v-if="Array.isArray(getNested(row, 'ghs_pictograms_urls'))">
			              <a 
			                v-for="(url, idx) in getNested(row, 'ghs_pictograms_urls')"
			                :key="idx"
			                :href="url"
			                target="_blank"
			                class="p-1 hover:bg-blue-50 rounded transition-colors"
			                :title="col.label"
			              >
			                <img :src="url" :alt="col.label" class="w-8 h-8 object-contain" />
			              </a>
			            </template>
			            <File v-else class="w-5 h-5 text-gray-300" />
			          </div>
					</template>
					<!-- Default many handling -->
					<template v-else>
						<Tooltip>
							<TooltipTrigger asChild>
								<div class="w-full min-w-0 truncate" :style="{ maxWidth: displayColumnWidth(col) + 'px' }">
									{{toCollection(getNested(row, col.key)).map(u => optionLabel(optionsForColumn(col), u, col.display_options)).join(', ')}}
								</div>
							</TooltipTrigger>
							<TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
								{{toCollection(getNested(row, col.key)).map(u => optionLabel(optionsForColumn(col), u, col.display_options)).join(', ')}}
							</TooltipContent>
						</Tooltip>
					</template>
				</template>
				
				<!-- Special handling for h_codes column (if it exists as a separate column) -->
				<template v-else-if="props.endpoint === 'gefahrstoffe' && col.key === 'h_codes'">
					<Tooltip>
						<TooltipTrigger>
							<div class="text-xs" :style="{ maxWidth: displayColumnWidth(col) + 'px' }">
								<div v-for="hp in (row.h_phrases ? toCollection(row.h_phrases) : [])" :key="hp.id" class="mb-1">
									<strong>{{ hp.h_code }}</strong> {{ hp.h_phrase }}
								</div>
							</div>
						</TooltipTrigger>
						<TooltipContent class="whitespace-pre-line text-xs max-w-[500px] break-words">
							<div v-for="hp in (row.h_phrases ? toCollection(row.h_phrases) : [])" :key="hp.id" class="mb-2">
								<strong>{{ hp.h_code }}:</strong> {{ hp.h_phrase }}
							</div>
						</TooltipContent>
					</Tooltip>
				</template>

		        <template v-else-if="col.type === 'color'">
		          <div class="flex items-center gap-2">
		            <div class="w-4 h-4 rounded-full border" :style="{ backgroundColor: getNested(row, col.key) }"></div>
		            <span class="text-xs text-gray-600">{{ getNested(row, col.key) }}</span>
		          </div>
		        </template>

		        <template v-else-if="col.type === 'date'">
		          {{
		            getNested(row, col.key)
		              ? new Date(getNested(row, col.key)).toLocaleDateString(dateLocale, {
		                  day: "2-digit",
		                  month: "2-digit",
		                  year: "numeric",
		                })
		              : ""
		          }}
		        </template>
				<template v-else-if="col.type === 'datetime'">
				  {{
					getNested(row, col.key)
					  ? new Date(getNested(row, col.key)).toLocaleString(dateLocale, {
					      day: "2-digit",
					      month: "2-digit",
					      year: "numeric",
					      hour: "2-digit",
					      minute: "2-digit",
					      second: "2-digit", // optional — remove if you don't want seconds
					    })
					  : ""
				  }}
				</template>
		        <template v-else-if="props.endpoint === 'gefahrstoffe' && col.type === 'text'">
		          <div
		            class="truncate"
		            :style="{ maxWidth: displayColumnWidth(col) + 'px' }"
		            :title="formatCellValue(getNested(row, col.key))"
		          >
		            {{ formatCellValue(getNested(row, col.key)) }}
		          </div>
		        </template>
		        <template v-else-if="col.type === 'text'">
		          <Tooltip>
		            <TooltipTrigger>
		              <div class="truncate" :style="{ maxWidth: displayColumnWidth(col) + 'px' }">
		                {{ formatCellValue(getNested(row, col.key)) }}
		              </div>
		            </TooltipTrigger>
		            <TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
		              {{ formatCellValue(getNested(row, col.key)) }}
		            </TooltipContent>
		          </Tooltip>
		        </template>

				<template v-else-if="col.edit === 'file' || col.type === 'file'">
		          <div class="flex items-center justify-center gap-1 flex-wrap">
		            <template v-if="Array.isArray(getNested(row, col.key))">
		              <a 
		                v-for="(url, idx) in getNested(row, col.key)"
		                :key="idx"
		                :href="url"
		                target="_blank"
		                class="p-1 hover:bg-blue-50 rounded transition-colors"
		                :title="col.label"
		              >
		                <img :src="url" :alt="col.label" class="w-8 h-8 object-contain" />
		              </a>
		            </template>
		            <template v-else-if="getNested(row, col.key)">
		              <a 
		                :href="getNested(row, col.key)"
		                target="_blank"
		                download
		                class="p-2 hover:bg-blue-50 rounded transition-colors"
		                :title="col.label"
		              >
		                <Download class="w-5 h-5 text-blue-600" />
		              </a>
		            </template>
		            <File v-else class="w-5 h-5 text-gray-300" />
		          </div>
		        </template>

		        <template v-else>
		          {{ formatCellValue(getNested(row, col.key)) }}
		        </template>
		      </div>
		    </TableCell>
		    </tr>
		  </template>
		</tbody>
      </table>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center p-4 bg-gray-50 border-t h-[65px]">
      <div class="flex gap-2 items-center">
        <label v-if="withpaging" class="text-sm">{{t('view.entriesperpage')}}:</label>
        <select v-if="withpaging" v-model.number="serverPageSize" @change="onPageSizeChange" class="border rounded p-1 text-sm">
          <option :value="5">5</option>
          <option :value="10">10</option>
          <option :value="20">20</option>
		  <option v-if="isGefahrstoffeTable" :value="25">25</option>
		  <option v-if="!isGefahrstoffeTable" :value="50">50</option>
	      <option v-if="!isGefahrstoffeTable" :value="100">100</option>
        </select>
      </div>
      <div v-if="withpaging" class="flex gap-2 items-center">
        <Button :style="{backgroundColor: serverPage === 1 ? 'gray' : 'black'}" size="sm" @click="goToPage(serverPage-1)">{{t('view.prev')}}</Button>
        <span v-if="totalRowsKnown" class="text-sm">{{t('view.page')}} {{ serverPage }} {{t('view.of')}} {{ totalPages }} ({{totalRows}} {{t('view.entries')}})</span>
        <span v-else class="text-sm">
          {{t('view.page')}} {{ serverPage }}
          <template v-if="countUnavailable">(Anzahl nicht verfügbar)</template>
          <template v-else>(Anzahl wird geladen…)</template>
        </span>
        <Button :style="{backgroundColor: serverPage === totalPages ? 'gray' : 'black'}" size="sm" @click="goToPage(serverPage+1)">{{t('view.next')}}</Button>
      </div>
    </div>

	<!-- Custom left-click context menu -->
    <div v-if="menuVisible"
      :style="{
      position: 'fixed',
      top: menuY + 'px',
      left: menuX + 'px',
      background: '#fff',
      border: '1px solid #ccc',
      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
      zIndex: 2000,
      minWidth: '120px',
    }"
    >
    <div v-if="props.enableCopy"
      @click="copyToClipboard"
      @mouseover="hoverCopyMenuItem = true"
      @mouseleave="hoverCopyMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverCopyMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.copy') }}
    </div>

	<div v-if="props.enableNotifications"
      @click="notifyStatus"
      @mouseover="hoverEmailMenuItem = true"
      @mouseleave="hoverEmailMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverEmailMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.sendemail') }}
    </div>

</div>

  </Card>
  
  <EditorPanel v-if="showEditor[props.endpoint]"
               :columns="editorColumnsForPanel"
			   :label="editorPanelTitle"
			   :readOnly="editorReadOnly"
			   :is-saving="!!editorSaving[String(props.endpoint)]"
			   :currentItem="currentItem[props.endpoint]"
			   :dynamicOptions="dynamicOptions"
			   endpoint="props.endpoint" 
			   @close-editor="entityStore.closeEditor(props.endpoint)"
			   @save-editor="saveEditorOptimistically"/>
</template>

<style scoped>
/* Table layout */
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.datatable-column-disabled {
  background-color: #f3f4f6 !important;
  color: #9ca3af !important;
  cursor: not-allowed;
}

:deep(.richtext div) {
    max-height: 40px !important;
	overflow: inherit !important;
}
/* Sticky rows already handled with classes in template */

/* Optional row animation */
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(-10px); }
.fade-slide-enter-to { opacity: 1; transform: translateY(0); }
.fade-slide-leave-from { opacity: 1; transform: translateY(0); }
.fade-slide-leave-to { opacity: 0; transform: translateY(10px); }

/* Highlight new row */
.new-row { animation: highlight 1s ease; }
@keyframes highlight { 0% { background-color: #e0f2fe; } 100% { background-color: transparent; } }

.th-label {
  overflow: hidden;
  white-space: normal;   /* allow wrapping */
  text-overflow: ellipsis; /* only works if combined with height/line-clamp */
  max-height: 2rem;       /* for example, 3 lines of text */
}

.datatable-spinner {
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: rgba(0, 0, 0, 0.55);
  animation: datatable-spin 0.8s linear infinite;
}

@keyframes datatable-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
